import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useToast } from '../components/Toast';
import { usePermissions } from '../hooks/usePermissions';
import { buildFirmAddress } from '../utils/firmAddress';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const DEPARTMENTS = [
  'GST & Indirect Tax', 'Income Tax & ITR', 'MCA & Company Law',
  'Accounts & Audit', 'TDS & Compliance', 'Administration', 'Management', 'Other'
];

const EMPLOYMENT_TYPES = [
  { value:'full_time', label:'Full Time' },
  { value:'part_time', label:'Part Time' },
  { value:'intern', label:'Intern' },
  { value:'contract', label:'Contract' },
  { value:'consultant', label:'Consultant' },
];

const BLOOD_GROUPS = ['A+','A-','B+','B-','AB+','AB-','O+','O-'];

const EMPTY_FORM = {
  user_id:'', full_name:'', personal_email:'', personal_mobile:'',
  emergency_contact_name:'', emergency_contact_mobile:'', emergency_contact_relation:'',
  date_of_birth:'', gender:'', blood_group:'', address:'', city:'', state:'', pincode:'',
  pan_number:'', aadhaar_number:'', date_of_joining:'', department:'', designation:'',
  employment_type:'full_time', reporting_to:'', work_location:'office', status:'active',
  bank_name:'', bank_account:'', bank_ifsc:'', bank_account_type:'', notes:'', attendance_mode:''
};

const EMPTY_SALARY = {
  ctc:0, basic:0, hra:0, travel_allowance:0, special_allowance:0, other_allowance:0,
  enable_epf:false, enable_esi:false, enable_tds:false, enable_pt:false,
  epf_employee_percent:12, epf_employer_percent:12,
  esi_employee_percent:0.75, esi_employer_percent:3.25,
  tds_monthly:0, pt_monthly:0, effective_from:''
};

function SmartDatePicker({ label, value, onChange, placeholder = 'dd/mm/yyyy' }) {
  const [open, setOpen] = React.useState(false);
  const [viewYear, setViewYear] = React.useState(() => value ? new Date(value).getFullYear() : new Date().getFullYear());
  const [viewMonth, setViewMonth] = React.useState(() => value ? new Date(value).getMonth() : new Date().getMonth());
  const [mode, setMode] = React.useState('day');
  const ref = React.useRef(null);
  React.useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const DAYS   = ['Su','Mo','Tu','We','Th','Fr','Sa'];
  const today  = new Date(); today.setHours(0,0,0,0);
  const selected = value ? new Date(value + 'T00:00:00') : null;
  const getDays = () => {
    const first = new Date(viewYear, viewMonth, 1).getDay();
    const total = new Date(viewYear, viewMonth + 1, 0).getDate();
    const prev  = new Date(viewYear, viewMonth, 0).getDate();
    const cells = [];
    for (let i = first - 1; i >= 0; i--) cells.push({ d: prev - i, curr: false });
    for (let d = 1; d <= total; d++) cells.push({ d, curr: true });
    while (cells.length % 7 !== 0) cells.push({ d: cells.length - first - total + 1, curr: false });
    return cells;
  };
  const selectDay = d => {
    const m = String(viewMonth + 1).padStart(2,'0'), dd = String(d).padStart(2,'0');
    onChange(`${viewYear}-${m}-${dd}`); setOpen(false);
  };
  const yearRange  = Array.from({ length: 12 }, (_, i) => viewYear - 5 + i);
  const displayVal = value ? new Date(value + 'T00:00:00').toLocaleDateString('en-IN',{ day:'2-digit', month:'short', year:'numeric' }) : '';
  return (
    <div className="relative" ref={ref}>
      {label && <label className="block text-xs font-medium text-gray-500 mb-1.5">{label}</label>}
      <button type="button" onClick={() => { setOpen(o => !o); setMode('day'); }}
        className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-left flex items-center justify-between hover:border-blue-400 focus:outline-none bg-white">
        <span className={displayVal ? 'text-gray-800' : 'text-gray-400'}>{displayVal || placeholder}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-[9999] w-72 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <button type="button" onClick={() => { if(mode==='day') setViewMonth(m => { if(m===0){setViewYear(y=>y-1);return 11;} return m-1; }); else if(mode==='year') setViewYear(y=>y-12); }} className="w-7 h-7 rounded-lg hover:bg-gray-100 flex items-center justify-center"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg></button>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => setMode(m => m==='month'?'day':'month')} className="px-2 py-1 rounded-lg hover:bg-gray-100 text-sm font-semibold text-gray-700">{MONTHS[viewMonth]}</button>
              <button type="button" onClick={() => setMode(m => m==='year'?'day':'year')}  className="px-2 py-1 rounded-lg hover:bg-gray-100 text-sm font-semibold text-gray-700">{viewYear}</button>
            </div>
            <button type="button" onClick={() => { if(mode==='day') setViewMonth(m => { if(m===11){setViewYear(y=>y+1);return 0;} return m+1; }); else if(mode==='year') setViewYear(y=>y+12); }} className="w-7 h-7 rounded-lg hover:bg-gray-100 flex items-center justify-center"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg></button>
          </div>
          {mode === 'day' && (
            <div className="p-3">
              <div className="grid grid-cols-7 mb-1">{DAYS.map(d=><div key={d} className="text-center text-xs font-semibold text-gray-400 py-1">{d}</div>)}</div>
              <div className="grid grid-cols-7 gap-0.5">
                {getDays().map((cell,i) => {
                  const date = cell.curr ? new Date(viewYear, viewMonth, cell.d) : null;
                  const isToday = date && date.getTime() === today.getTime();
                  const isSel   = date && selected && date.getTime() === selected.getTime();
                  return <button key={i} type="button" onClick={() => cell.curr && selectDay(cell.d)} className={`h-8 w-full rounded-lg text-xs transition ${!cell.curr?'text-gray-300 cursor-default':isSel?'text-white font-bold':isToday?'font-bold border border-blue-400 text-blue-600':'hover:bg-gray-100 text-gray-700'}`} style={isSel?{background:'#0f1f45'}:{}}>{cell.d}</button>;
                })}
              </div>
              <div className="flex justify-center mt-2 pt-2 border-t border-gray-100">
                <button type="button" onClick={() => { const t=new Date(); setViewYear(t.getFullYear()); setViewMonth(t.getMonth()); onChange(t.toISOString().split('T')[0]); setOpen(false); }} className="text-xs font-medium px-3 py-1 rounded-lg hover:bg-gray-100 text-blue-600">Today</button>
                {value && <button type="button" onClick={() => { onChange(''); setOpen(false); }} className="text-xs font-medium px-3 py-1 rounded-lg hover:bg-gray-100 text-red-400 ml-2">Clear</button>}
              </div>
            </div>
          )}
          {mode === 'month' && <div className="grid grid-cols-3 gap-2 p-4">{MONTHS.map((m,i)=><button key={m} type="button" onClick={()=>{setViewMonth(i);setMode('day');}} className={`py-2 rounded-lg text-sm transition ${viewMonth===i?'text-white font-bold':'hover:bg-gray-100 text-gray-700'}`} style={viewMonth===i?{background:'#0f1f45'}:{}}>{m}</button>)}</div>}
          {mode === 'year'  && <div className="grid grid-cols-3 gap-2 p-4">{yearRange.map(y=><button key={y} type="button" onClick={()=>{setViewYear(y);setMode('day');}} className={`py-2 rounded-lg text-sm transition ${viewYear===y?'text-white font-bold':'hover:bg-gray-100 text-gray-700'}`} style={viewYear===y?{background:'#0f1f45'}:{}}>{y}</button>)}</div>}
        </div>
      )}
    </div>
  );
}

const DEPT_COLORS = {
  'GST & Indirect Tax':    'bg-blue-50 text-blue-700',
  'Income Tax & ITR':      'bg-green-50 text-green-700',
  'MCA & Company Law':     'bg-purple-50 text-purple-700',
  'Accounts & Audit':      'bg-amber-50 text-amber-700',
  'TDS & Compliance':      'bg-orange-50 text-orange-700',
  'Administration':        'bg-gray-50 text-gray-600',
  'Management':            'bg-indigo-50 text-indigo-700',
  'Other':                 'bg-slate-50 text-slate-600',
};

const STATUS_CFG = {
  active:      { label:'Active',      cls:'bg-green-50 text-green-700 border-green-200' },
  inactive:    { label:'Inactive',    cls:'bg-gray-100 text-gray-500 border-gray-200'  },
  on_notice:   { label:'On Notice',   cls:'bg-amber-50 text-amber-700 border-amber-200'},
  terminated:  { label:'Terminated',  cls:'bg-red-50 text-red-600 border-red-200'      },
};

const fc = n => `₹${Number(n||0).toLocaleString('en-IN',{minimumFractionDigits:2})}`;

// ── TOGGLE COMPONENT ─────────────────────────────────────────────────────────
function Toggle({ value, onChange, label }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer select-none">
      <div onClick={() => onChange(!value)}
        className={`w-11 h-6 rounded-full flex items-center transition-colors relative ${value ? 'bg-blue-600' : 'bg-gray-200'}`}>
        <div className={`w-4 h-4 bg-white rounded-full shadow absolute transition-all ${value ? 'left-6' : 'left-1'}`}/>
      </div>
      {label && <span className="text-sm text-gray-700">{label}</span>}
    </label>
  );
}

// ── SECTION HEADER ───────────────────────────────────────────────────────────
function SectionHeader({ num, title }) {
  return (
    <div className="flex items-center gap-3 mb-4 mt-7 first:mt-0">
      <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
        style={{ background:'#0f1f45' }}>{num}</div>
      <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wide">{title}</h4>
      <div className="flex-1 h-px bg-gray-100"/>
    </div>
  );
}

// ── FIELD WRAPPER ────────────────────────────────────────────────────────────
function Field({ label, children, className='' }) {
  return (
    <div className={className}>
      {label && <label className="block text-xs font-medium text-gray-500 mb-1.5">{label}</label>}
      {children}
    </div>
  );
}

// ── MAIN HR COMPONENT ────────────────────────────────────────────────────────
function HR({ token, darkMode = false, T: theme }) {
  const T = theme || { bg:'#f4f7ff', bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', cardBg:'#fff', cardBorder:'#f1f5f9', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [activeTab, setActiveTab] = useState('employees');
  const headers = { 'Authorization': `Bearer ${token}` };

  const tabs = [
    { key:'employees',  label:'Employees',
      icon:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg> },
    { key:'attendance', label:'Attendance',
      icon:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><polyline points="9 16 11 18 15 14"/></svg> },
    { key:'timesheets', label:'Timesheets',
      icon:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg> },
    { key:'leaves',     label:'Leaves',
      icon:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg> },
    { key:'payroll',    label:'Payroll',
      icon:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg> },
  ];

  return (
    <div style={{
      minHeight:'100vh', padding:24,
      background: darkMode
        ? 'radial-gradient(ellipse at 80% 0%,rgba(249,115,22,0.07) 0%,transparent 60%),#0a0f1e'
        : 'radial-gradient(ellipse at 80% 0%,rgba(249,115,22,0.05) 0%,transparent 50%),#f4f7ff',
    }}>
      {/* Tabs */}
      <div style={{ display:'flex', alignItems:'center', gap:4, marginBottom:24, background:T.bg2, border:`1px solid ${T.border}`, borderRadius:16, padding:6, width:'fit-content', boxShadow: darkMode?'0 2px 12px rgba(0,0,0,0.3)':'0 1px 4px rgba(15,31,69,0.06)' }}>
        {tabs.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
            display:'flex', alignItems:'center', gap:7, padding:'8px 16px', borderRadius:12,
            fontSize:13, fontWeight:600, cursor:'pointer', border:'none', transition:'all 0.15s',
            background: activeTab===tab.key ? 'linear-gradient(135deg,#0f1f45,#1a3a6b)' : 'transparent',
            color: activeTab===tab.key ? '#fff' : T.text3,
            boxShadow: activeTab===tab.key ? '0 2px 8px rgba(15,31,69,0.3)' : 'none',
          }}>
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'employees'  && <EmployeesTab  token={token} headers={headers} darkMode={darkMode} T={T} />}
      {activeTab === 'attendance' && <AttendanceTab token={token} headers={headers} darkMode={darkMode} T={T} />}
      {activeTab === 'timesheets' && <TimesheetsTab headers={headers} darkMode={darkMode} T={T} />}
      {activeTab === 'leaves'     && <LeavesTab     token={token} headers={headers} darkMode={darkMode} T={T} />}
      {activeTab === 'payroll'    && <PayrollTab    token={token} headers={headers} darkMode={darkMode} T={T} />}
    </div>
  );
}

// ── COMING SOON PLACEHOLDERS ─────────────────────────────────────────────────
function AttendanceTab({ token, headers, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', cardBg:'#fff', cardBorder:'#f1f5f9', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const is = { border:`1px solid ${T.inputBorder}`, borderRadius:9, padding:'8px 12px', fontSize:13, outline:'none', background:T.inputBg, color:T.text };
  const jh = { 'Content-Type':'application/json', ...headers };
  const { showToast } = useToast();
  const { scope: permScope } = usePermissions();
  const canEdit = permScope('hr.attendance_mark') === 'all';   // may mark attendance for anyone
  const now = new Date();
  const [month,   setMonth]   = useState(now.getMonth() + 1);
  const [year,    setYear]    = useState(now.getFullYear());
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { emp, day }
  const [editForm, setEditForm] = useState({ status:'present', punch_in:'', punch_out:'', notes:'' });
  const [saving,  setSaving]  = useState(false);

  const STATUS_CFG = {
    present:  { label:'P',  bg:'#dcfce7', text:'#16a34a', full:'Present'  },
    absent:   { label:'A',  bg:'#fee2e2', text:'#dc2626', full:'Absent'   },
    half_day: { label:'H',  bg:'#fef9c3', text:'#ca8a04', full:'Half Day' },
    wfh:      { label:'W',  bg:'#dbeafe', text:'#2563eb', full:'WFH'      },
    leave:    { label:'L',  bg:'#ede9fe', text:'#7c3aed', full:'Leave'    },
    holiday:  { label:'H',  bg:'#f3f4f6', text:'#6b7280', full:'Holiday'  },
    weekend:  { label:'—',  bg:'#f9fafb', text:'#d1d5db', full:'Weekend'  },
    future:   { label:'·',  bg:'#ffffff', text:'#e5e7eb', full:'Future'   },
    pending:  { label:'?',  bg:'#f8fafc', text:'#94a3b8', full:'Not marked' },
    pre_join: { label:'',   bg:'repeating-linear-gradient(45deg,#f1f5f9,#f1f5f9 3px,#e2e8f0 3px,#e2e8f0 6px)', text:'#cbd5e1', full:'Before joining' },
  };

  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const YEARS  = Array.from({ length:5 }, (_,i) => now.getFullYear() - 2 + i);

  const fetchAttendance = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/api/employees/attendance/monthly?month=${month}&year=${year}`, { headers });
      const d   = await res.json();
      setData(d);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [month, year, token]);

  useEffect(() => { fetchAttendance(); }, [fetchAttendance]);

  const openEdit = (emp, day, dayData) => {
    if (!canEdit) return;
    setEditing({ emp, day, dayData });
    setEditForm({
      status:    ['weekend','holiday','future','absent'].includes(dayData.status) && !dayData.is_offday_worked
                   ? 'present' : dayData.status,
      punch_in:  dayData.punch_in  || '',
      punch_out: dayData.punch_out || '',
      notes:     ''
    });
  };

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const dateStr = `${year}-${String(month).padStart(2,'0')}-${String(editing.day).padStart(2,'0')}`;
      await fetch(`${BASE}/api/employees/attendance/mark`, {
        method: 'POST', headers: jh,
        body: JSON.stringify({
          employee_id:     editing.emp.id,
          attendance_date: dateStr,
          status:          editForm.status,
          punch_in:        editForm.punch_in  || null,
          punch_out:       editForm.punch_out || null,
          notes:           editForm.notes
        })
      });
      setEditing(null);
      showToast('Attendance updated', 'success');
      fetchAttendance();
    } catch(e) { showToast('Error saving attendance', 'error'); }
    finally { setSaving(false); }
  };

  const exportExcel = () => {
    if (!data) return;
    const XLSX = window.XLSX || require('xlsx');
    const days = Array.from({ length: data.days_in_month }, (_,i) => i+1);
    const rows = data.employees.map(e => {
      const row = { 'Employee': e.full_name, 'ID': e.employee_id, 'Department': e.department };
      days.forEach(d => {
        const dd = e.days[d];
        row[`${d}`] = dd?.status === 'leave' && dd?.leave_code ? dd.leave_code : (STATUS_CFG[dd?.status]?.label || '');
      });
      row['Present'] = e.summary.present;
      row['Absent']  = e.summary.absent;
      row['WFH']     = e.summary.wfh;
      row['Leave']   = e.summary.leave;
      return row;
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `Attendance ${MONTHS[month-1]} ${year}`);
    XLSX.writeFile(wb, `Attendance_${MONTHS[month-1]}_${year}.xlsx`);
  };

  const totalPresent = data?.employees?.reduce((s,e) => s + e.summary.present, 0) || 0;
  const totalAbsent  = data?.employees?.reduce((s,e) => s + e.summary.absent,  0) || 0;
  const totalWFH     = data?.employees?.reduce((s,e) => s + e.summary.wfh,     0) || 0;
  const totalLeave   = data?.employees?.reduce((s,e) => s + e.summary.leave,   0) || 0;

  const days = data ? Array.from({ length: data.days_in_month }, (_,i) => i+1) : [];

  // Per-day classification (weekend / holiday / working) — firm-level, from the API.
  const dayTypes = data?.day_types || {};
  const holidayNames = data?.holiday_names || {};
  const totalOffdayWorked = data?.employees?.reduce((s,e) => s + (e.summary.offday_worked||0), 0) || 0;
  const totalLate = data?.employees?.reduce((s,e) => s + (e.summary.late||0), 0) || 0;
  const totalOT   = Math.round((data?.employees?.reduce((s,e) => s + (e.summary.overtime_hours||0), 0) || 0) * 10) / 10;
  const statCards = [
    { label:'Employees', value: data?.employees?.length||0, color:'#3b82f6', glow:'rgba(59,130,246,0.12)' },
    { label:'Present',   value: totalPresent, color:'#16a34a', glow:'rgba(22,163,74,0.12)' },
    { label:'Absent',    value: totalAbsent,  color:'#dc2626', glow:'rgba(220,38,38,0.12)' },
    { label:'WFH',       value: totalWFH,     color:'#2563eb', glow:'rgba(37,99,235,0.12)' },
    { label:'On Leave',  value: totalLeave,   color:'#7c3aed', glow:'rgba(124,58,237,0.12)' },
    { label:'Off-day',   value: totalOffdayWorked, color:'#f97316', glow:'rgba(249,115,22,0.12)' },
    ...(data?.rules?.late_arrival ? [{ label:'Late',   value: totalLate, color:'#f59e0b', glow:'rgba(245,158,11,0.12)' }] : []),
    ...(data?.rules?.overtime     ? [{ label:'OT hrs', value: totalOT,   color:'#6d28d9', glow:'rgba(109,40,217,0.12)' }] : []),
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
        <div>
          <h2 style={{ fontSize:15, fontWeight:700, color:T.text, margin:0 }}>Attendance</h2>
          <p style={{ fontSize:12, color:T.text3, marginTop:3 }}>Monthly register · {canEdit ? 'click any cell to edit' : 'view only — ask an admin or manager to edit'}</p>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <select value={month} onChange={e=>setMonth(Number(e.target.value))} style={{ ...is, minWidth:90 }}>
            {MONTHS.map((m,i) => <option key={i+1} value={i+1}>{m}</option>)}
          </select>
          <select value={year} onChange={e=>setYear(Number(e.target.value))} style={is}>
            {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={exportExcel} style={{
            display:'flex', alignItems:'center', gap:6, padding:'8px 14px',
            borderRadius:9, border:`1.5px solid ${T.border2}`, background:T.bg2,
            color:T.text2, fontSize:13, fontWeight:500, cursor:'pointer',
          }}
          onMouseEnter={e=>{e.currentTarget.style.borderColor='#f97316';e.currentTarget.style.color='#f97316';}}
          onMouseLeave={e=>{e.currentTarget.style.borderColor=T.border2;e.currentTarget.style.color=T.text2;}}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Export
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:`repeat(${statCards.length},1fr)`, gap:10, marginBottom:18 }}>
        {statCards.map(s => (
          <div key={s.label} style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:14, padding:'12px 16px', boxShadow:`0 4px 12px ${s.glow}`, position:'relative', overflow:'hidden' }}>
            <div style={{ position:'absolute', top:-12, right:-12, width:40, height:40, borderRadius:'50%', background:s.glow, filter:'blur(8px)' }}/>
            <p style={{ fontSize:20, fontWeight:800, color:s.color, margin:0 }}>{s.value}</p>
            <p style={{ fontSize:11, color:T.text3, marginTop:4 }}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* Legend */}
      <div style={{ display:'flex', alignItems:'center', gap:16, marginBottom:12, flexWrap:'wrap' }}>
        {Object.entries(STATUS_CFG).filter(([k])=>{
          if (['future','holiday'].includes(k)) return false;
          if (k === 'pending' && data?.rules?.auto_absent !== false) return false;
          return true;
        }).map(([k,v]) => (
          <div key={k} style={{ display:'flex', alignItems:'center', gap:6 }}>
            <div style={{ width:24, height:24, borderRadius:6, display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700, background:v.bg, color:v.text }}>{v.label}</div>
            <span style={{ fontSize:12, color:T.text3 }}>{v.full}</span>
          </div>
        ))}
        {data?.rules?.late_arrival && (
          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
            <span style={{ width:9, height:9, borderRadius:'50%', background:'#f59e0b', border:'1px solid #fff', boxShadow:'0 0 0 1px #f59e0b' }}/>
            <span style={{ fontSize:12, color:T.text3 }}>Late arrival</span>
          </div>
        )}
        {data?.rules?.overtime && (
          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
            <span style={{ width:9, height:9, borderRadius:'50%', background:'#7c3aed', border:'1px solid #fff', boxShadow:'0 0 0 1px #7c3aed' }}/>
            <span style={{ fontSize:12, color:T.text3 }}>Overtime</span>
          </div>
        )}
      </div>

      {/* Grid */}
      {loading ? (
        <div style={{ background:T.bg2, borderRadius:14, border:`1px solid ${T.cardBorder}`, padding:'64px 0', textAlign:'center', color:T.text3, fontSize:13 }}>Loading attendance...</div>
      ) : !data?.employees?.length ? (
        <div style={{ background:T.bg2, borderRadius:14, border:`1px solid ${T.cardBorder}`, padding:'64px 0', textAlign:'center' }}>
          <p style={{ fontSize:14, fontWeight:600, color:T.text2, margin:0 }}>No active employees found</p>
          <p style={{ fontSize:12, color:T.text3, marginTop:5 }}>Onboard employees first</p>
        </div>
      ) : (
        <div style={{ background:T.bg2, borderRadius:14, border:`1px solid ${T.cardBorder}`, overflow:'hidden', boxShadow: darkMode?'0 4px 24px rgba(0,0,0,0.3)':'0 2px 12px rgba(15,31,69,0.06)' }}>
          <div style={{ overflowX:'auto', scrollbarWidth:'thin' }}>
            <table style={{ borderCollapse:'collapse', minWidth:900 }}>
              <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)' }}>
                <tr>
                  <th style={{ position:'sticky', left:0, background:'#0f1f45', padding:'10px 16px', textAlign:'left', fontSize:11, fontWeight:700, color:'#fff', minWidth:176, zIndex:10, borderRight:`1px solid rgba(255,255,255,0.1)` }}>
                    Employee
                  </th>
                  {days.map(d => {
                    const dateObj = new Date(year, month-1, d);
                    const dt = dayTypes[d];
                    const isOff = dt === 'weekend' || dt === 'holiday';
                    const isToday   = dateObj.toDateString() === new Date().toDateString();
                    return (
                      <th key={d} title={dt === 'holiday' ? (holidayNames[d] || 'Holiday') : dt === 'weekend' ? 'Weekly off' : ''}
                        style={{ padding:'6px 2px', textAlign:'center', fontSize:11, fontWeight:700, color: isOff?'rgba(255,255,255,0.35)':'#fff', width:32, background: isToday?'rgba(59,130,246,0.3)':dt==='holiday'?'rgba(249,115,22,0.25)':'transparent' }}>
                        <div>{d}</div>
                        <div style={{ fontSize:9, fontWeight:400, opacity:0.6 }}>{['Su','Mo','Tu','We','Th','Fr','Sa'][dateObj.getDay()]}</div>
                      </th>
                    );
                  })}
                  {['P','A','W','L'].map((h,i) => (
                    <th key={h} style={{ padding:'10px 8px', textAlign:'center', fontSize:11, fontWeight:700, color:'#fff', borderLeft: i===0?`1px solid rgba(255,255,255,0.1)`:'none' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.employees.map((emp, ei) => (
                  <tr key={emp.id} style={{ borderBottom:`1px solid ${T.border}`, background: darkMode?(ei%2===0?T.bg2:T.bg3):(ei%2===0?'#fff':'#fafbff') }}>
                    <td style={{ position:'sticky', left:0, padding:'8px 16px', borderRight:`1px solid ${T.border}`, zIndex:5, background: darkMode?(ei%2===0?T.bg2:T.bg3):(ei%2===0?'#fff':'#fafbff') }}>
                      <p style={{ fontSize:12, fontWeight:600, color:T.text, margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:144 }}>{emp.full_name}</p>
                      <p style={{ fontSize:11, color:T.text3, fontFamily:'monospace', margin:'2px 0 0' }}>{emp.employee_id}</p>
                    </td>
                    {days.map(d => {
                      const dayData = emp.days[d] || { status:'absent' };
                      const cfg = STATUS_CFG[dayData.status] || STATUS_CFG.absent;
                      const isEditable = canEdit && !['future','pre_join'].includes(dayData.status);
                      const offWorked = dayData.is_offday_worked;
                      const cellLabel = dayData.status === 'leave' && dayData.leave_code ? dayData.leave_code : cfg.label;
                      const otH = dayData.overtime_minutes ? Math.round(dayData.overtime_minutes/6)/10 : 0;
                      return (
                        <td key={d} style={{ padding:'4px 2px', textAlign:'center' }}>
                          <div onClick={() => isEditable && openEdit(emp, d, dayData)}
                            title={(dayData.holiday_name ? dayData.holiday_name + ' · ' : '') + (dayData.status === 'leave' && dayData.leave_code ? dayData.leave_code + ' — ' : '') + cfg.full + (offWorked?' (worked on off-day)':'') + (dayData.is_late?` · Late ${dayData.late_minutes}m`:'') + (otH?` · OT ${otH}h`:'') + (dayData.punch_in?` · In: ${dayData.punch_in}`:'') + (dayData.punch_out?` Out: ${dayData.punch_out}`:'') + (dayData.from_log?' (auto)':'') + (isEditable?' · click to edit':'')}
                            style={{ position:'relative', width:28, height:28, borderRadius:6, display:'flex', alignItems:'center', justifyContent:'center', fontSize: cellLabel.length > 1 ? 9 : 11, fontWeight:700, margin:'0 auto', cursor: isEditable?'pointer':'default', background:cfg.bg, color:cfg.text, boxShadow: offWorked?'0 0 0 2px #f97316':'none', transition:'transform 0.1s, opacity 0.1s' }}
                            onMouseEnter={e => { if(isEditable){ e.currentTarget.style.transform='scale(1.15)'; e.currentTarget.style.opacity='0.85'; } }}
                            onMouseLeave={e => { e.currentTarget.style.transform='scale(1)'; e.currentTarget.style.opacity='1'; }}>
                            {cellLabel}
                            {dayData.is_late && <span style={{ position:'absolute', top:-2, right:-2, width:7, height:7, borderRadius:'50%', background:'#f59e0b', border:'1px solid #fff' }}/>}
                            {otH > 0 && <span style={{ position:'absolute', bottom:-2, right:-2, width:7, height:7, borderRadius:'50%', background:'#7c3aed', border:'1px solid #fff' }}/>}
                          </div>
                        </td>
                      );
                    })}
                    <td style={{ padding:'8px', textAlign:'center', fontSize:12, fontWeight:700, color:'#16a34a', borderLeft:`1px solid ${T.border}` }}>{emp.summary.present}</td>
                    <td style={{ padding:'8px', textAlign:'center', fontSize:12, fontWeight:700, color:'#dc2626' }}>{emp.summary.absent}</td>
                    <td style={{ padding:'8px', textAlign:'center', fontSize:12, fontWeight:700, color:'#2563eb' }}>{emp.summary.wfh}</td>
                    <td style={{ padding:'8px', textAlign:'center', fontSize:12, fontWeight:700, color:'#7c3aed' }}>{emp.summary.leave}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editing && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:50, padding:16 }}>
          <div style={{ background:T.bg2, borderRadius:20, width:'100%', maxWidth:420, boxShadow: darkMode?'0 24px 60px rgba(0,0,0,0.6)':'0 24px 60px rgba(15,31,69,0.2)' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'16px 24px', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', borderRadius:'20px 20px 0 0' }}>
              <div>
                <h3 style={{ fontSize:14, fontWeight:700, color:'#fff', margin:0 }}>Edit Attendance</h3>
                <p style={{ fontSize:12, color:'rgba(255,255,255,0.6)', margin:'3px 0 0' }}>{editing.emp.full_name} · {String(editing.day).padStart(2,'0')} {MONTHS[month-1]} {year}</p>
              </div>
              <button onClick={()=>setEditing(null)} style={{ width:30, height:30, borderRadius:8, border:'none', background:'rgba(255,255,255,0.1)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div style={{ padding:'20px 24px', background:T.bg3 }}>
              {editing.dayData?.day_type && editing.dayData.day_type !== 'working' && (
                <div style={{ display:'flex', gap:8, alignItems:'flex-start', padding:'10px 12px', borderRadius:10, background:'rgba(249,115,22,0.1)', border:'1px solid rgba(249,115,22,0.3)', marginBottom:16 }}>
                  <span style={{ fontSize:14 }}>⚠️</span>
                  <p style={{ fontSize:11.5, color:'#c2410c', margin:0, lineHeight:1.5 }}>
                    This is a {editing.dayData.day_type === 'holiday' ? (editing.dayData.holiday_name || 'holiday') : 'weekly off'}.
                    Marking <b>Present / Half Day / WFH</b> records it as off-day working (eligible for overtime / comp-off).
                  </p>
                </div>
              )}
              {(editing.dayData?.is_late || editing.dayData?.overtime_minutes > 0) && (
                <div style={{ display:'flex', gap:12, marginBottom:16, fontSize:11.5 }}>
                  {editing.dayData?.is_late && <span style={{ color:'#b45309', fontWeight:600 }}>⏰ Late by {editing.dayData.late_minutes}m</span>}
                  {editing.dayData?.overtime_minutes > 0 && <span style={{ color:'#6d28d9', fontWeight:600 }}>+ OT {Math.round(editing.dayData.overtime_minutes/6)/10}h</span>}
                </div>
              )}
              <div style={{ marginBottom:16 }}>
                <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:8, textTransform:'uppercase', letterSpacing:'0.05em' }}>Status</label>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8 }}>
                  {['present','absent','half_day','wfh','leave','holiday'].map(s => {
                    const cfg = STATUS_CFG[s];
                    return (
                      <button key={s} onClick={()=>setEditForm(f=>({...f,status:s}))}
                        style={{ padding:'8px 0', borderRadius:10, fontSize:12, fontWeight:700, cursor:'pointer', background:cfg.bg, color:cfg.text, border: editForm.status===s?`2px solid ${cfg.text}`:'2px solid transparent', transition:'all 0.15s' }}>
                        {cfg.full}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:14 }}>
                <div>
                  <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>Punch In</label>
                  <input type="time" value={editForm.punch_in} onChange={e=>setEditForm(f=>({...f,punch_in:e.target.value}))} style={{ ...is, width:'100%', boxSizing:'border-box' }} />
                </div>
                <div>
                  <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>Punch Out</label>
                  <input type="time" value={editForm.punch_out} onChange={e=>setEditForm(f=>({...f,punch_out:e.target.value}))} style={{ ...is, width:'100%', boxSizing:'border-box' }} />
                </div>
              </div>
              <div>
                <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>Notes</label>
                <input type="text" placeholder="Optional note..." value={editForm.notes} onChange={e=>setEditForm(f=>({...f,notes:e.target.value}))} style={{ ...is, width:'100%', boxSizing:'border-box' }} />
              </div>
            </div>
            <div style={{ display:'flex', justifyContent:'flex-end', gap:10, padding:'14px 24px', borderTop:`1px solid ${T.border}`, background:T.bg2, borderRadius:'0 0 20px 20px' }}>
              <button onClick={()=>setEditing(null)} style={{ padding:'8px 18px', borderRadius:9, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, cursor:'pointer' }}
                onMouseEnter={e=>e.currentTarget.style.background=T.bg3} onMouseLeave={e=>e.currentTarget.style.background=T.bg2}>Cancel</button>
              <button onClick={handleSave} disabled={saving} style={{ display:'flex', alignItems:'center', gap:7, padding:'8px 22px', borderRadius:9, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', opacity:saving?0.6:1 }}>
                {saving && <span style={{ width:13, height:13, border:'2px solid #fff', borderTopColor:'transparent', borderRadius:'50%', display:'inline-block', animation:'spin 0.6s linear infinite' }}/>}
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AttendanceComingSoon() {
  return <PlaceholderTab icon="🗓️" title="Attendance" desc="Monthly attendance grid, auto punch logs, and admin override — coming soon." />;
}
function LeavesTab({ token, headers, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', cardBg:'#fff', cardBorder:'#f1f5f9', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const is = { width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:10, padding:'9px 12px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' };
  const jh = { 'Content-Type':'application/json', ...headers };
  const { showToast, showConfirm } = useToast();
  const [payChoice, setPayChoice] = useState(null);

  const [activeView,  setActiveView]  = useState('applications');
  const [applications,setApplications]= useState([]);
  const [balances,    setBalances]    = useState([]);
  const [employees,   setEmployees]   = useState([]);
  const [leaveTypes,  setLeaveTypes]  = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [filterStatus,setFilterStatus]= useState('all');
  const [showForm,    setShowForm]    = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [showReject,  setShowReject]  = useState(null);
  const [rejectReason,setRejectReason]= useState('');

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const { can: permCan, isAdmin: isFirmAdmin } = usePermissions();
  const isAdmin = permCan('hr.leave_approve');   // may approve / reject leave
  const [myEmployee, setMyEmployee] = useState(null);

  const [form, setForm] = useState({
    employee_id:'', leave_type_id:'', from_date:'', to_date:'', days:'', reason:''
  });
  const DOW = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];
  const [workDays, setWorkDays] = useState(['monday','tuesday','wednesday','thursday','friday','saturday']);
  const [holidaySet, setHolidaySet] = useState(new Set());

  const STATUS_CFG = {
    pending:  { label:'Pending',  bg:'bg-amber-50',  text:'text-amber-700',  border:'border-amber-200' },
    approved: { label:'Approved', bg:'bg-green-50',  text:'text-green-700',  border:'border-green-200' },
    rejected: { label:'Rejected', bg:'bg-red-50',    text:'text-red-700',    border:'border-red-200'   },
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch current user's employee record if staff
      if (!isAdmin) {
        const meRes = await fetch(`${BASE}/api/employees/my/punch-status`, { headers });
        const meData = await meRes.json();
        if (meData.is_employee && meData.employee_id) {
          const empRes2 = await fetch(`${BASE}/api/employees/${meData.employee_id}`, { headers });
          const empData = await empRes2.json();
          setMyEmployee(empData);
          setForm(f => ({ ...f, employee_id: String(meData.employee_id) }));
        }
      }
      const yr = new Date().getFullYear();
      const [appRes, balRes, empRes, ltRes, setRes, holRes] = await Promise.all([
        fetch(`${BASE}/api/employees/leaves/all`,      { headers }),
        fetch(`${BASE}/api/employees/leaves/balances`, { headers }),
        fetch(`${BASE}/api/employees`,                 { headers }),
        fetch(`${BASE}/api/employees/leave-types`,     { headers }),
        fetch(`${BASE}/api/attendance-settings`,       { headers }),
        fetch(`${BASE}/api/holidays?year=${yr}`,       { headers }),
      ]);
      const [apps, bals, emps, lts, aset, hols] = await Promise.all([
        appRes.json(), balRes.json(), empRes.json(), ltRes.json(), setRes.json(), holRes.json()
      ]);
      setApplications(Array.isArray(apps) ? apps : []);
      setBalances(Array.isArray(bals) ? bals : []);
      setEmployees(Array.isArray(emps) ? emps.filter(e=>e.status==='active') : []);
      setLeaveTypes(Array.isArray(lts) ? lts : []);
      let wd = aset?.working_days;
      if (typeof wd === 'string') { try { wd = JSON.parse(wd); } catch { wd = null; } }
      if (Array.isArray(wd) && wd.length) setWorkDays(wd);
      setHolidaySet(new Set((Array.isArray(hols)?hols:[]).filter(h=>!h.is_optional).map(h => new Date(h.holiday_date).toDateString())));
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Auto calculate days when dates change
  useEffect(() => {
    if (form.from_date && form.to_date) {
      const from = new Date(form.from_date);
      const to   = new Date(form.to_date);
      if (to >= from) {
        let days = 0;
        const cur = new Date(from);
        while (cur <= to) {
          const isWorking = workDays.includes(DOW[cur.getDay()]);
          const isHoliday = holidaySet.has(cur.toDateString());
          if (isWorking && !isHoliday) days++;
          cur.setDate(cur.getDate() + 1);
        }
        setForm(f => ({ ...f, days: String(days) }));
      }
    }
  }, [form.from_date, form.to_date, workDays, holidaySet]);

  const handleApply = async () => {
    if (!form.employee_id || !form.leave_type_id || !form.from_date || !form.to_date)
      return showToast('Please fill all required fields', 'error');
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/api/employees/leaves/apply`, {
        method:'POST', headers:jh, body:JSON.stringify(form)
      });
      const data = await res.json();
      if (!res.ok) return showToast(data.message || 'Error', 'error');
      setShowForm(false);
      setForm({ employee_id:'', leave_type_id:'', from_date:'', to_date:'', days:'', reason:'' });
      showToast('Leave application submitted', 'success');
      fetchAll();
    } catch(e) { showToast('Error applying leave', 'error'); }
    finally { setSaving(false); }
  };

  const handleAction = async (id, action, reason='', pay_type=null) => {
    try {
      const res = await fetch(`${BASE}/api/employees/leaves/${id}/action`, {
        method:'PUT', headers:jh,
        body: JSON.stringify({ action, rejection_reason: reason, pay_type })
      });
      const d = await res.json().catch(()=>({}));
      if (!res.ok) return showToast(d.message || 'Error', 'error');
      setShowReject(null); setRejectReason(''); setPayChoice(null);
      showToast(`Leave ${action}d`, 'success');
      fetchAll();
    } catch(e) { showToast('Error', 'error'); }
  };

  // Approve: if the type defers pay to approval, ask first.
  const startApprove = (a) => {
    if (a.type_pay_mode === 'on_approval') setPayChoice(a);
    else handleAction(a.id, 'approved');
  };

  const isSuperAdmin = isFirmAdmin;   // deleting a leave application stays admin-only
  const handleDeleteLeave = async (a) => {
    const ok = await showConfirm(
      `Delete this ${a.leave_type_name} application for ${a.employee_name} (${a.days} day${a.days===1?'':'s'})?${a.status==='approved' ? ' The days will be added back to their balance.' : ''}`,
      { title:'Delete Leave Application', confirmLabel:'Delete', confirmColor:'#dc2626' }
    );
    if (!ok) return;
    try {
      const res = await fetch(`${BASE}/api/employees/leaves/${a.id}`, { method:'DELETE', headers });
      const d = await res.json().catch(()=>({}));
      if (!res.ok) return showToast(d.message || 'Error', 'error');
      showToast('Leave application deleted', 'success');
      fetchAll();
    } catch(e) { showToast('Error', 'error'); }
  };

  const filtered = applications.filter(a =>
    filterStatus === 'all' || a.status === filterStatus
  );

  const pending  = applications.filter(a => a.status === 'pending').length;
  const approved = applications.filter(a => a.status === 'approved').length;
  const rejected = applications.filter(a => a.status === 'rejected').length;

  // Group balances by employee
  const balByEmp = {};
  balances.forEach(b => {
    if (!balByEmp[b.employee_id]) balByEmp[b.employee_id] = { name: b.full_name, emp_id: b.emp_id, dept: b.department, types: [] };
    balByEmp[b.employee_id].types.push(b);
  });

  const stBadge = (status) => {
    const s = {
      pending:  { c:'#d97706', bg: darkMode?'rgba(245,158,11,0.12)':'#fffbeb',  border: darkMode?'rgba(245,158,11,0.25)':'#fde68a',   label:'Pending'  },
      approved: { c:'#16a34a', bg: darkMode?'rgba(22,163,74,0.12)':'#f0fdf4',   border: darkMode?'rgba(22,163,74,0.25)':'#bbf7d0',    label:'Approved' },
      rejected: { c:'#dc2626', bg: darkMode?'rgba(220,38,38,0.12)':'#fef2f2',   border: darkMode?'rgba(220,38,38,0.25)':'#fecaca',    label:'Rejected' },
    };
    return s[status] || s.pending;
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
        <div>
          <h2 style={{ fontSize:15, fontWeight:700, color:T.text, margin:0 }}>Leave Management</h2>
          <p style={{ fontSize:12, color:T.text3, marginTop:3 }}>{pending} pending approval</p>
        </div>
        <button onClick={() => setShowForm(true)} style={{ display:'flex', alignItems:'center', gap:7, padding:'9px 18px', borderRadius:10, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', boxShadow:'0 4px 12px rgba(15,31,69,0.3)' }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Apply Leave
        </button>
      </div>

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:18 }}>
        {[
          { label:'Total Applications', value: applications.length, color:'#3b82f6', glow:'rgba(59,130,246,0.12)'  },
          { label:'Pending',  value: pending,  color:'#d97706', glow:'rgba(245,158,11,0.12)' },
          { label:'Approved', value: approved, color:'#16a34a', glow:'rgba(22,163,74,0.12)'  },
          { label:'Rejected', value: rejected, color:'#dc2626', glow:'rgba(220,38,38,0.12)'  },
        ].map(s => (
          <div key={s.label} style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:14, padding:'14px 18px', boxShadow:`0 4px 12px ${s.glow}`, position:'relative', overflow:'hidden' }}>
            <div style={{ position:'absolute', top:-12, right:-12, width:40, height:40, borderRadius:'50%', background:s.glow, filter:'blur(8px)' }}/>
            <p style={{ fontSize:22, fontWeight:800, color:s.color, margin:0 }}>{s.value}</p>
            <p style={{ fontSize:11, color:T.text3, marginTop:4 }}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* View switcher */}
      <div style={{ display:'flex', alignItems:'center', gap:4, marginBottom:18, background:T.bg2, border:`1px solid ${T.border}`, borderRadius:14, padding:6, width:'fit-content' }}>
        {[
          { key:'applications', label:'Applications' },
          { key:'balances',     label:'Leave Balances' },
        ].map(v => (
          <button key={v.key} onClick={() => setActiveView(v.key)} style={{
            padding:'7px 16px', borderRadius:10, fontSize:13, fontWeight:600, border:'none', cursor:'pointer', transition:'all 0.15s',
            background: activeView===v.key?'linear-gradient(135deg,#0f1f45,#1a3a6b)':'transparent',
            color: activeView===v.key?'#fff':T.text3,
          }}>{v.label}</button>
        ))}
      </div>

      {/* APPLICATIONS VIEW */}
      {activeView === 'applications' && (
        <>
          {/* Filter pills */}
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:14, flexWrap:'wrap' }}>
            {['all','pending','approved','rejected'].map(s => {
              const active = filterStatus===s;
              const count = s==='all'?applications.length:applications.filter(a=>a.status===s).length;
              return (
                <button key={s} onClick={() => setFilterStatus(s)} style={{
                  padding:'6px 14px', borderRadius:20, fontSize:12, fontWeight:600, cursor:'pointer', transition:'all 0.15s', textTransform:'capitalize',
                  background: active?(darkMode?'rgba(59,130,246,0.15)':'#eff6ff'):'transparent',
                  color: active?'#3b82f6':T.text3,
                  border: active?`1.5px solid ${darkMode?'rgba(59,130,246,0.3)':'#bfdbfe'}`:`1.5px solid ${T.border2}`,
                }}>
                  {s==='all'?`All (${count})`:`${s.charAt(0).toUpperCase()+s.slice(1)} (${count})`}
                </button>
              );
            })}
          </div>

          <div style={{ background:T.bg2, borderRadius:14, border:`1px solid ${T.cardBorder}`, overflow:'hidden', boxShadow: darkMode?'0 4px 24px rgba(0,0,0,0.3)':'0 2px 12px rgba(15,31,69,0.06)' }}>
            {loading ? (
              <div style={{ padding:'64px 0', textAlign:'center', color:T.text3, fontSize:13 }}>Loading...</div>
            ) : filtered.length === 0 ? (
              <div style={{ padding:'56px 0', textAlign:'center' }}>
                <p style={{ fontSize:14, fontWeight:600, color:T.text2, margin:0 }}>No leave applications found</p>
                <p style={{ fontSize:12, color:T.text3, marginTop:5 }}>Apply a leave to get started</p>
              </div>
            ) : (
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)' }}>
                  <tr>
                    {['Employee','Leave Type','Duration','Days','Reason','Status','Actions'].map(h => (
                      <th key={h} style={{ padding:'10px 16px', textAlign:'left', fontSize:11, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((a,i) => {
                    const st = stBadge(a.status);
                    return (
                      <tr key={a.id} style={{ borderBottom:`1px solid ${T.border}`, background: darkMode?(i%2===0?T.bg2:T.bg3):(i%2===0?'#fff':'#fafbff'), transition:'background 0.15s' }}
                        onMouseEnter={e=>e.currentTarget.style.background=darkMode?'#1e3a5f':'#f0f4ff'}
                        onMouseLeave={e=>e.currentTarget.style.background=darkMode?(i%2===0?T.bg2:T.bg3):(i%2===0?'#fff':'#fafbff')}>
                        <td style={{ padding:'11px 16px' }}>
                          <p style={{ fontSize:13, fontWeight:600, color:T.text, margin:0 }}>{a.employee_name}</p>
                          <p style={{ fontSize:11, color:T.text3, margin:'2px 0 0' }}>{a.emp_id} · {a.department}</p>
                        </td>
                        <td style={{ padding:'11px 16px' }}>
                          <span style={{ fontSize:11, fontWeight:700, padding:'3px 8px', borderRadius:7, background: darkMode?'rgba(59,130,246,0.12)':'#eff6ff', color:'#2563eb' }}>{a.leave_type_code}</span>
                          <p style={{ fontSize:11, color:T.text3, marginTop:3 }}>{a.leave_type_name}</p>
                        </td>
                        <td style={{ padding:'11px 16px', fontSize:12, color:T.text3, whiteSpace:'nowrap' }}>
                          {new Date(a.from_date).toLocaleDateString('en-IN',{day:'2-digit',month:'short'})}
                          {' → '}
                          {new Date(a.to_date).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}
                        </td>
                        <td style={{ padding:'11px 16px' }}>
                          <span style={{ fontSize:14, fontWeight:700, color:T.text }}>{a.days}</span>
                          <span style={{ fontSize:11, color:T.text3, marginLeft:4 }}>days</span>
                        </td>
                        <td style={{ padding:'11px 16px', maxWidth:160 }}>
                          <p style={{ fontSize:12, color:T.text2, margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a.reason||'—'}</p>
                          {a.rejection_reason && <p style={{ fontSize:11, color:'#ef4444', marginTop:3, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>Reason: {a.rejection_reason}</p>}
                        </td>
                        <td style={{ padding:'11px 16px' }}>
                          <span style={{ fontSize:11, fontWeight:600, padding:'3px 9px', borderRadius:20, color:st.c, background:st.bg, border:`1px solid ${st.border}` }}>{st.label}</span>
                          {a.approved_by_name && <p style={{ fontSize:11, color:T.text3, marginTop:3 }}>by {a.approved_by_name}</p>}
                        </td>
                        <td style={{ padding:'11px 16px' }}>
                          <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                            {a.status === 'pending' && (<>
                              <button onClick={() => startApprove(a)} style={{ padding:'5px 10px', borderRadius:8, fontSize:12, fontWeight:500, cursor:'pointer', color:'#16a34a', background: darkMode?'rgba(22,163,74,0.1)':'#f0fdf4', border:`1px solid ${darkMode?'rgba(22,163,74,0.25)':'#bbf7d0'}` }}
                                onMouseEnter={e=>e.currentTarget.style.background=darkMode?'rgba(22,163,74,0.2)':'#dcfce7'}
                                onMouseLeave={e=>e.currentTarget.style.background=darkMode?'rgba(22,163,74,0.1)':'#f0fdf4'}>Approve</button>
                              <button onClick={() => { setShowReject(a.id); setRejectReason(''); }} style={{ padding:'5px 10px', borderRadius:8, fontSize:12, fontWeight:500, cursor:'pointer', color:'#dc2626', background: darkMode?'rgba(220,38,38,0.1)':'#fef2f2', border:`1px solid ${darkMode?'rgba(220,38,38,0.25)':'#fecaca'}` }}
                                onMouseEnter={e=>e.currentTarget.style.background=darkMode?'rgba(220,38,38,0.2)':'#fee2e2'}
                                onMouseLeave={e=>e.currentTarget.style.background=darkMode?'rgba(220,38,38,0.1)':'#fef2f2'}>Reject</button>
                            </>)}
                            {isSuperAdmin && (
                              <button onClick={() => handleDeleteLeave(a)} title="Delete application (admin)"
                                style={{ width:28, height:28, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color:'#94a3b8', background:'none', border:`1px solid ${T.border2}` }}
                                onMouseEnter={e=>{e.currentTarget.style.color='#dc2626';e.currentTarget.style.borderColor='#fecaca';}}
                                onMouseLeave={e=>{e.currentTarget.style.color='#94a3b8';e.currentTarget.style.borderColor=T.border2;}}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* BALANCES VIEW */}
      {activeView === 'balances' && (
        <div style={{ background:T.bg2, borderRadius:14, border:`1px solid ${T.cardBorder}`, overflow:'hidden', boxShadow: darkMode?'0 4px 24px rgba(0,0,0,0.3)':'0 2px 12px rgba(15,31,69,0.06)' }}>
          {Object.keys(balByEmp).length === 0 ? (
            <div style={{ padding:'56px 0', textAlign:'center' }}>
              <p style={{ fontSize:14, fontWeight:600, color:T.text2, margin:0 }}>No leave balances found</p>
              <p style={{ fontSize:12, color:T.text3, marginTop:5 }}>Onboard employees to initialize balances</p>
            </div>
          ) : (
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)' }}>
                <tr>
                  <th style={{ padding:'10px 16px', textAlign:'left', fontSize:11, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em' }}>Employee</th>
                  {leaveTypes.map(lt => (
                    <th key={lt.id} style={{ padding:'10px 16px', textAlign:'center', fontSize:11, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em' }}>
                      {lt.code}
                      <div style={{ fontSize:10, fontWeight:400, opacity:0.7, textTransform:'none' }}>{lt.name}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.values(balByEmp).map((emp, i) => (
                  <tr key={emp.emp_id} style={{ borderBottom:`1px solid ${T.border}`, background: darkMode?(i%2===0?T.bg2:T.bg3):(i%2===0?'#fff':'#fafbff'), transition:'background 0.15s' }}
                    onMouseEnter={e=>e.currentTarget.style.background=darkMode?'#1e3a5f':'#f0f4ff'}
                    onMouseLeave={e=>e.currentTarget.style.background=darkMode?(i%2===0?T.bg2:T.bg3):(i%2===0?'#fff':'#fafbff')}>
                    <td style={{ padding:'12px 16px' }}>
                      <p style={{ fontSize:13, fontWeight:600, color:T.text, margin:0 }}>{emp.name}</p>
                      <p style={{ fontSize:11, color:T.text3, margin:'2px 0 0' }}>{emp.emp_id} · {emp.dept}</p>
                    </td>
                    {leaveTypes.map(lt => {
                      const bal = emp.types.find(t => t.leave_type_id === lt.id);
                      const used    = bal?.used_days    || 0;
                      const total   = bal?.total_days   || lt.days_per_year;
                      const balance = bal?.balance_days ?? (total - used);
                      const pendingDays = bal?.pending_days || 0;
                      const pct = total > 0 ? (used/total)*100 : 0;
                      return (
                        <td key={lt.id} style={{ padding:'12px 16px', textAlign:'center' }}>
                          <p style={{ fontSize:18, fontWeight:800, color: balance<=2?'#dc2626':T.text, margin:0 }}>{balance}</p>
                          <p style={{ fontSize:11, color:T.text3 }}>{used}/{total} used</p>
                          {pendingDays>0 && <p style={{ fontSize:11, color:'#d97706' }}>{pendingDays} pending</p>}
                          <div style={{ width:'100%', maxWidth:60, background: darkMode?'rgba(255,255,255,0.08)':'#e5e7eb', borderRadius:20, height:4, margin:'5px auto 0' }}>
                            <div style={{ height:4, borderRadius:20, background:'#3b82f6', width:`${Math.min(pct,100)}%` }}/>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* APPLY LEAVE MODAL */}
      {showForm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:50, padding:16 }}>
          <div style={{ background:T.bg2, borderRadius:20, width:'100%', maxWidth:520, boxShadow: darkMode?'0 24px 60px rgba(0,0,0,0.6)':'0 24px 60px rgba(15,31,69,0.2)' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'18px 24px', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', borderRadius:'20px 20px 0 0' }}>
              <div>
                <h3 style={{ fontSize:15, fontWeight:700, color:'#fff', margin:0 }}>Apply Leave</h3>
                <p style={{ fontSize:12, color:'rgba(255,255,255,0.6)', margin:'3px 0 0' }}>Days auto-calculated (excluding weekly-offs &amp; public holidays)</p>
              </div>
              <button onClick={()=>setShowForm(false)} style={{ width:30, height:30, borderRadius:8, border:'none', background:'rgba(255,255,255,0.1)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}
                onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.2)'}
                onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,0.1)'}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div style={{ padding:'20px 24px', background:T.bg3, display:'flex', flexDirection:'column', gap:14 }}>
              {isAdmin ? (
                <div>
                  <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>Employee *</label>
                  <select value={form.employee_id} onChange={e=>setForm(f=>({...f,employee_id:e.target.value}))} style={is}>
                    <option value="">Select employee...</option>
                    {employees.map(e=><option key={e.id} value={e.id}>{e.full_name} ({e.employee_id})</option>)}
                  </select>
                </div>
              ) : (
                <div>
                  <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>Employee</label>
                  <div style={{ border:`1px solid ${T.inputBorder}`, borderRadius:10, padding:'10px 14px', background:T.bg2, display:'flex', alignItems:'center', gap:12 }}>
                    <div style={{ width:32, height:32, borderRadius:'50%', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:13, fontWeight:700, flexShrink:0 }}>
                      {myEmployee?.full_name?.charAt(0)?.toUpperCase() || currentUser?.full_name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div>
                      <p style={{ fontSize:13, fontWeight:600, color:T.text, margin:0 }}>{myEmployee?.full_name || currentUser?.full_name}</p>
                      <p style={{ fontSize:11, color:T.text3, margin:'2px 0 0' }}>{myEmployee?.employee_id} · {myEmployee?.department}</p>
                    </div>
                  </div>
                </div>
              )}
              <div>
                <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>Leave Type *</label>
                <select value={form.leave_type_id} onChange={e=>setForm(f=>({...f,leave_type_id:e.target.value}))} style={is}>
                  <option value="">Select leave type...</option>
                  {leaveTypes.map(lt=><option key={lt.id} value={lt.id}>{lt.name} ({lt.code})</option>)}
                </select>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <SmartDatePicker label="From Date *" value={form.from_date} onChange={v=>setForm(f=>({...f,from_date:v}))} placeholder="Select from date" darkMode={darkMode} T={T} />
                <SmartDatePicker label="To Date *"   value={form.to_date}   onChange={v=>setForm(f=>({...f,to_date:v}))}   placeholder="Select to date"   darkMode={darkMode} T={T} />
              </div>
              {form.days && (
                <div style={{ background: darkMode?'rgba(59,130,246,0.1)':'#eff6ff', border:`1px solid ${darkMode?'rgba(59,130,246,0.2)':'#bfdbfe'}`, borderRadius:10, padding:'12px 16px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                  <span style={{ fontSize:13, color:'#2563eb' }}>Working days calculated</span>
                  <span style={{ fontSize:16, fontWeight:800, color:'#2563eb' }}>{form.days} days</span>
                </div>
              )}
              <div>
                <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>Reason</label>
                <textarea rows="3" placeholder="Reason for leave..." value={form.reason} onChange={e=>setForm(f=>({...f,reason:e.target.value}))} style={{ ...is, resize:'none' }} />
              </div>
            </div>
            <div style={{ display:'flex', justifyContent:'flex-end', gap:10, padding:'14px 24px', borderTop:`1px solid ${T.border}`, background:T.bg2, borderRadius:'0 0 20px 20px' }}>
              <button onClick={()=>setShowForm(false)} style={{ padding:'9px 18px', borderRadius:9, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, cursor:'pointer' }}
                onMouseEnter={e=>e.currentTarget.style.background=T.bg3} onMouseLeave={e=>e.currentTarget.style.background=T.bg2}>Cancel</button>
              <button onClick={handleApply} disabled={saving} style={{ display:'flex', alignItems:'center', gap:7, padding:'9px 22px', borderRadius:9, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', opacity:saving?0.6:1, boxShadow:'0 4px 12px rgba(15,31,69,0.3)' }}>
                {saving && <span style={{ width:13, height:13, border:'2px solid #fff', borderTopColor:'transparent', borderRadius:'50%', display:'inline-block', animation:'spin 0.6s linear infinite' }}/>}
                {saving ? 'Applying...' : 'Apply Leave'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* REJECT MODAL */}
      {showReject && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:50, padding:16 }}>
          <div style={{ background:T.bg2, borderRadius:16, width:'100%', maxWidth:420, boxShadow: darkMode?'0 24px 60px rgba(0,0,0,0.6)':'0 24px 60px rgba(15,31,69,0.2)' }}>
            <div style={{ padding:'16px 20px', borderBottom:`1px solid ${T.border}` }}>
              <h3 style={{ fontSize:14, fontWeight:700, color:T.text, margin:0 }}>Reject Leave</h3>
              <p style={{ fontSize:12, color:T.text3, marginTop:3 }}>Provide a reason for rejection</p>
            </div>
            <div style={{ padding:'16px 20px' }}>
              <textarea rows="3" placeholder="Reason for rejection..." value={rejectReason} onChange={e=>setRejectReason(e.target.value)}
                style={{ width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:10, padding:'9px 12px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, resize:'none', boxSizing:'border-box' }} />
            </div>
            <div style={{ display:'flex', justifyContent:'flex-end', gap:10, padding:'12px 20px', borderTop:`1px solid ${T.border}`, background:T.bg2, borderRadius:'0 0 16px 16px' }}>
              <button onClick={()=>setShowReject(null)} style={{ padding:'8px 16px', borderRadius:8, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, cursor:'pointer' }}
                onMouseEnter={e=>e.currentTarget.style.background=T.bg3} onMouseLeave={e=>e.currentTarget.style.background=T.bg2}>Cancel</button>
              <button onClick={() => handleAction(showReject, 'rejected', rejectReason)} style={{ padding:'8px 18px', borderRadius:8, border:'none', background:'linear-gradient(135deg,#dc2626,#b91c1c)', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer' }}>
                Confirm Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* APPROVE — PAY TREATMENT CHOICE (types set to "Decide at approval") */}
      {payChoice && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:50, padding:16 }}>
          <div style={{ background:T.bg2, borderRadius:16, width:'100%', maxWidth:440, boxShadow: darkMode?'0 24px 60px rgba(0,0,0,0.6)':'0 24px 60px rgba(15,31,69,0.2)' }}>
            <div style={{ padding:'16px 20px', borderBottom:`1px solid ${T.border}` }}>
              <h3 style={{ fontSize:14, fontWeight:700, color:T.text, margin:0 }}>Approve {payChoice.leave_type_name}</h3>
              <p style={{ fontSize:12, color:T.text3, marginTop:3 }}>{payChoice.employee_name} · {payChoice.days} day{payChoice.days===1?'':'s'} · how should this be paid?</p>
            </div>
            <div style={{ padding:'16px 20px', display:'flex', flexDirection:'column', gap:10 }}>
              {[
                { v:'paid',   label:'Paid — full',  desc:'No salary deduction for these days.',        c:'#16a34a' },
                { v:'half',   label:'Paid — half',  desc:'Half of each day is deducted as Loss of Pay.', c:'#d97706' },
                { v:'unpaid', label:'Unpaid',       desc:'Deducted as Loss of Pay (after the monthly free quota).', c:'#dc2626' },
              ].map(o => (
                <button key={o.v} onClick={() => handleAction(payChoice.id, 'approved', '', o.v)}
                  style={{ textAlign:'left', padding:'11px 14px', borderRadius:10, border:`1px solid ${T.border2}`, background:T.bg3, cursor:'pointer' }}
                  onMouseEnter={e=>e.currentTarget.style.borderColor=o.c}
                  onMouseLeave={e=>e.currentTarget.style.borderColor=T.border2}>
                  <p style={{ fontSize:13, fontWeight:700, color:o.c, margin:0 }}>{o.label}</p>
                  <p style={{ fontSize:11, color:T.text3, margin:'2px 0 0' }}>{o.desc}</p>
                </button>
              ))}
            </div>
            <div style={{ display:'flex', justifyContent:'flex-end', padding:'12px 20px', borderTop:`1px solid ${T.border}` }}>
              <button onClick={()=>setPayChoice(null)} style={{ padding:'8px 16px', borderRadius:8, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, cursor:'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

}

function LeavesComingSoon() {
  return <PlaceholderTab icon="✏️" title="Leave Management" desc="Apply leave, approval workflow, leave balances and calendar — coming soon." />;
}
function PayrollTab({ token, headers, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', cardBg:'#fff', cardBorder:'#f1f5f9', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const is = { width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:10, padding:'9px 12px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' };
  const jh = { 'Content-Type':'application/json', ...headers };
  const { showToast, showConfirm } = useToast();
  const MONTHS_LIST = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const now = new Date();

  const [months,      setMonths]      = useState([]);
  const [selectedPM,  setSelectedPM]  = useState(null);
  const [slips,       setSlips]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [generating,  setGenerating]  = useState(false);
  const [editSlip,    setEditSlip]    = useState(null);
  const [editForm,    setEditForm]    = useState({ bonus:0, advance_deduction:0, other_deductions:0, notes:'' });
  const [saving,      setSaving]      = useState(false);
  const [showGenForm, setShowGenForm] = useState(false);
  const [genForm,     setGenForm]     = useState({
    month: now.getMonth()+1, year: now.getFullYear()
  });
  // Firm letterhead (logo, name, address, brand color) — the one place a firm
  // sets this is Settings → Billing Settings; every generated document,
  // payslips included, reads it from here instead of hardcoding a name.
  const [firmSettings, setFirmSettings] = useState({});
  useEffect(() => {
    fetch(`${BASE}/api/invoices/settings/config`, { headers })
      .then(r => r.json()).then(d => setFirmSettings(d || {})).catch(() => {});
  }, [token]);

  const fc = n => `₹${Number(n||0).toLocaleString('en-IN',{minimumFractionDigits:2})}`;

  const STATUS_CFG = {
    draft: { label:'Draft', cls:'bg-gray-100 text-gray-600 border-gray-200' },
    paid:  { label:'Paid',  cls:'bg-green-50 text-green-700 border-green-200' },
  };

  const fetchMonths = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BASE}/api/employees/payroll/months`, { headers });
      const d = await r.json();
      setMonths(Array.isArray(d) ? d : []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchMonths(); }, [fetchMonths]);

  const fetchSlips = async (pmId) => {
    try {
      const r = await fetch(`${BASE}/api/employees/payroll/months/${pmId}/slips`, { headers });
      const d = await r.json();
      setSlips(Array.isArray(d) ? d : []);
    } catch(e) { console.error(e); }
  };

  const handleSelectMonth = (pm) => {
    setSelectedPM(pm);
    fetchSlips(pm.id);
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${BASE}/api/employees/payroll/generate`, {
        method:'POST', headers:jh, body:JSON.stringify(genForm)
      });
      const data = await res.json();
      if (!res.ok) return showToast(data.message || 'Error', 'error');
      setShowGenForm(false);
      showToast('Payroll generated', 'success');
      await fetchMonths();
      // Auto-select generated month
      const r2 = await fetch(`${BASE}/api/employees/payroll/months`, { headers });
      const months2 = await r2.json();
      setMonths(Array.isArray(months2) ? months2 : []);
      const found = months2.find(m => m.month===genForm.month && m.year===genForm.year);
      if (found) { setSelectedPM(found); fetchSlips(found.id); }
    } catch(e) { showToast('Error generating payroll', 'error'); }
    finally { setGenerating(false); }
  };

  const handleMarkPaid = async () => {
    if (!selectedPM) return;
    const ok = await showConfirm(
      `Mark all salaries for ${MONTHS_LIST[selectedPM.month-1]} ${selectedPM.year} as PAID? This cannot be undone.`,
      { title:'Confirm Payroll Payout', confirmLabel:'Mark Paid', confirmColor:'#16a34a' }
    );
    if (!ok) return;
    await fetch(`${BASE}/api/employees/payroll/months/${selectedPM.id}/mark-paid`, { method:'PUT', headers:jh });
    showToast('Payroll marked as paid', 'success');
    fetchMonths();
    fetchSlips(selectedPM.id);
    setSelectedPM(p => ({ ...p, status:'paid' }));
  };

  const openEditSlip = (slip) => {
    setEditSlip(slip);
    setEditForm({
      bonus: slip.bonus||0,
      advance_deduction: slip.advance_deduction||0,
      other_deductions: slip.other_deductions||0,
      notes: slip.notes||''
    });
  };

  const handleSaveSlip = async () => {
    if (!editSlip) return;
    setSaving(true);
    try {
      await fetch(`${BASE}/api/employees/payroll/slips/${editSlip.id}`, {
        method:'PUT', headers:jh, body:JSON.stringify(editForm)
      });
      setEditSlip(null);
      showToast('Payslip updated', 'success');
      fetchSlips(selectedPM.id);
    } catch(e) { showToast('Error saving', 'error'); }
    finally { setSaving(false); }
  };

  const handlePrintSlip = (slip, pm) => {
    const w = window.open('', '_blank');
    const monthName = MONTHS_LIST[(pm.month||1)-1];
    // Letterhead — pulled from Settings → Billing Settings so every firm's
    // payslip shows its own name/logo/address, never a hardcoded one.
    const pColor  = firmSettings.primary_color || '#0f1f45';
    const firmName = firmSettings.firm_name || 'ChanakyaX';
    const firmAddr = buildFirmAddress(firmSettings);
    const firmContact = [firmSettings.firm_mobile ? `Mobile: ${firmSettings.firm_mobile}` : '', firmSettings.firm_email || ''].filter(Boolean).join('  |  ');
    w.document.write(`<!DOCTYPE html><html><head><title>Payslip - ${slip.full_name}</title>
    <style>
      @page{size:A4;margin:15mm}
      body{font-family:"Plus Jakarta Sans",Arial,sans-serif;margin:0;padding:0;color:#1a1a1a;font-size:12px}
      .header{background:${pColor};color:#fff;padding:20px 24px;display:flex;justify-content:space-between;align-items:flex-start}
      .firm-logo{max-height:36px;max-width:150px;object-fit:contain;margin-bottom:8px;display:block}
      .firm{font-size:16px;font-weight:bold}
      .firm-sub{font-size:10px;opacity:0.8;margin-top:3px}
      .slip-title{font-size:18px;font-weight:bold;text-align:right}
      .month-badge{font-size:11px;opacity:0.8;margin-top:4px;text-align:right}
      .sep{height:4px;background:${pColor}}
      .emp-section{display:grid;grid-template-columns:1fr 1fr;gap:16px;padding:16px 24px;border-bottom:1px solid #eee}
      .label{font-size:10px;color:#888;text-transform:uppercase;letter-spacing:0.4px;margin-bottom:3px}
      .value{font-size:13px;font-weight:bold;color:#1a1a1a}
      .earnings-section{padding:0 24px}
      table{width:100%;border-collapse:collapse;margin:0}
      th{background:${pColor};color:#fff;padding:8px 12px;text-align:left;font-size:10px;font-weight:600}
      th.r{text-align:right}
      td{padding:8px 12px;border-bottom:1px solid #eee;font-size:11px}
      td.r{text-align:right}
      .total-row td{font-weight:bold;font-size:13px;background:#f9f9f9}
      .net-row{background:${pColor};color:#fff}
      .net-row td{color:#fff;font-weight:bold;font-size:14px;padding:10px 12px}
      .footer{padding:14px 24px;display:grid;grid-template-columns:1.5fr 1fr;gap:14px;border-top:1px solid #eee;margin-top:8px}
      .sig{text-align:center;font-size:10px}
    </style></head><body>
    <div class="header">
      <div>
        ${firmSettings.logo_url ? `<img src="${firmSettings.logo_url}" class="firm-logo" alt="Logo"/>` : ''}
        <div class="firm">${firmName}</div>
        ${firmAddr ? `<div class="firm-sub">${firmAddr}</div>` : ''}
        ${firmContact ? `<div class="firm-sub">${firmContact}</div>` : ''}
        <div class="firm-sub" style="margin-top:6px">Payslip for the month of ${monthName} ${pm.year}</div>
      </div>
      <div><div class="slip-title">PAYSLIP</div><div class="month-badge">${monthName} ${pm.year}</div></div>
    </div>
    <div class="sep"></div>
    <div class="emp-section">
      <div><div class="label">Employee Name</div><div class="value">${slip.full_name}</div></div>
      <div><div class="label">Employee ID</div><div class="value">${slip.emp_id}</div></div>
      <div style="margin-top:10px"><div class="label">Department</div><div class="value">${slip.department||'—'}</div></div>
      <div style="margin-top:10px"><div class="label">Designation</div><div class="value">${slip.designation||'—'}</div></div>
      <div style="margin-top:10px"><div class="label">Working Days</div><div class="value">${slip.working_days}</div></div>
      <div style="margin-top:10px"><div class="label">Present Days</div><div class="value">${slip.present_days}</div></div>
    </div>
    <div class="earnings-section">
      <table>
        <thead><tr><th>Earnings</th><th class="r">Amount (₹)</th><th>Deductions</th><th class="r">Amount (₹)</th></tr></thead>
        <tbody>
          <tr><td>Basic Pay</td><td class="r">${Number(slip.basic||0).toLocaleString('en-IN',{minimumFractionDigits:2})}</td><td>${Number(slip.epf_employee||0)>0?'EPF (Employee)':'—'}</td><td class="r">${Number(slip.epf_employee||0)>0?Number(slip.epf_employee).toLocaleString('en-IN',{minimumFractionDigits:2}):'—'}</td></tr>
          <tr><td>HRA</td><td class="r">${Number(slip.hra||0).toLocaleString('en-IN',{minimumFractionDigits:2})}</td><td>${Number(slip.esi_employee||0)>0?'ESI (Employee)':'—'}</td><td class="r">${Number(slip.esi_employee||0)>0?Number(slip.esi_employee).toLocaleString('en-IN',{minimumFractionDigits:2}):'—'}</td></tr>
          <tr><td>Travel Allowance</td><td class="r">${Number(slip.travel_allowance||0).toLocaleString('en-IN',{minimumFractionDigits:2})}</td><td>${Number(slip.tds||0)>0?'TDS':'—'}</td><td class="r">${Number(slip.tds||0)>0?Number(slip.tds).toLocaleString('en-IN',{minimumFractionDigits:2}):'—'}</td></tr>
          <tr><td>Special Allowance</td><td class="r">${Number(slip.special_allowance||0).toLocaleString('en-IN',{minimumFractionDigits:2})}</td><td>${Number(slip.pt||0)>0?'Professional Tax':'—'}</td><td class="r">${Number(slip.pt||0)>0?Number(slip.pt).toLocaleString('en-IN',{minimumFractionDigits:2}):'—'}</td></tr>
          ${Number(slip.other_allowance||0)>0?`<tr><td>Other Allowance</td><td class="r">${Number(slip.other_allowance).toLocaleString('en-IN',{minimumFractionDigits:2})}</td><td>—</td><td class="r">—</td></tr>`:''}
          ${Number(slip.bonus||0)>0?`<tr><td>Bonus</td><td class="r">${Number(slip.bonus).toLocaleString('en-IN',{minimumFractionDigits:2})}</td><td>—</td><td class="r">—</td></tr>`:''}
          ${Number(slip.lop_amount||0)>0?`<tr><td>—</td><td class="r">—</td><td>Loss of Pay (${slip.lop_days} day${Number(slip.lop_days)===1?'':'s'})</td><td class="r">${Number(slip.lop_amount).toLocaleString('en-IN',{minimumFractionDigits:2})}</td></tr>`:''}
          ${Number(slip.advance_deduction||0)>0?`<tr><td>—</td><td class="r">—</td><td>Advance Recovery</td><td class="r">${Number(slip.advance_deduction).toLocaleString('en-IN',{minimumFractionDigits:2})}</td></tr>`:''}
          ${Number(slip.other_deductions||0)>0?`<tr><td>—</td><td class="r">—</td><td>Other Deductions</td><td class="r">${Number(slip.other_deductions).toLocaleString('en-IN',{minimumFractionDigits:2})}</td></tr>`:''}
          <tr class="total-row"><td>Gross Salary</td><td class="r">${Number(slip.gross_salary||0).toLocaleString('en-IN',{minimumFractionDigits:2})}</td><td>Total Deductions</td><td class="r">${Number(slip.total_deductions||0).toLocaleString('en-IN',{minimumFractionDigits:2})}</td></tr>
        </tbody>
      </table>
      <table style="margin-top:2px"><tbody>
        <tr class="net-row"><td>Net Salary Payable</td><td class="r" style="color:#fff;font-weight:bold;font-size:16px">₹${Number(slip.net_salary||0).toLocaleString('en-IN',{minimumFractionDigits:2})}</td></tr>
      </tbody></table>
    </div>
    ${slip.bank_account?`<div style="padding:10px 24px;font-size:10px;color:#555;border-top:1px solid #eee;margin-top:8px">Bank: <strong>${slip.bank_name||''}</strong> &nbsp;|&nbsp; A/C: <strong>${slip.bank_account}</strong> &nbsp;|&nbsp; IFSC: <strong>${slip.bank_ifsc||''}</strong></div>`:''}
    <div class="footer">
      <div style="font-size:9px;color:#555;line-height:1.8"><strong>Note:</strong> This is a system-generated payslip. For any discrepancy, contact HR.</div>
      <div class="sig"><div style="width:100px;height:30px;border-bottom:1px solid #999;margin:0 auto 4px"></div><div style="font-weight:bold;font-size:10px">Authorised Signature</div><div style="font-size:9px;color:#555">For ${firmName}</div></div>
    </div>
    <script>window.onload=function(){window.print()}<\/script>
    </body></html>`);
    w.document.close();
  };

  return (
    <div style={{ display:'flex', gap:20 }}>

      {/* LEFT — Payroll months list */}
      <div style={{ width:272, flexShrink:0 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
          <h2 style={{ fontSize:15, fontWeight:700, color:T.text, margin:0 }}>Payroll Months</h2>
          <button onClick={() => setShowGenForm(true)} style={{ display:'flex', alignItems:'center', gap:6, padding:'7px 14px', borderRadius:9, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:12, fontWeight:600, cursor:'pointer' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Run Payroll
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign:'center', color:T.text3, fontSize:13, padding:'40px 0' }}>Loading...</div>
        ) : months.length === 0 ? (
          <div style={{ background:T.bg2, borderRadius:14, border:`1px solid ${T.cardBorder}`, padding:'24px 16px', textAlign:'center' }}>
            <p style={{ fontSize:13, fontWeight:600, color:T.text2, margin:0 }}>No payroll runs yet</p>
            <p style={{ fontSize:11, color:T.text3, marginTop:5 }}>Click "Run Payroll" to generate</p>
          </div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {months.map(pm => {
              const isSel = selectedPM?.id === pm.id;
              const stDraft = pm.status === 'paid';
              return (
                <div key={pm.id} onClick={() => handleSelectMonth(pm)}
                  style={{ background:T.bg2, border:`2px solid ${isSel?'#3b82f6':T.cardBorder}`, borderRadius:14, padding:'14px 16px', cursor:'pointer', transition:'all 0.15s', boxShadow: isSel?'0 4px 16px rgba(59,130,246,0.15)':`0 2px 8px rgba(15,31,69,0.05)` }}
                  onMouseEnter={e=>{ if(!isSel) e.currentTarget.style.borderColor=T.border2; }}
                  onMouseLeave={e=>{ if(!isSel) e.currentTarget.style.borderColor=T.cardBorder; }}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
                    <p style={{ fontSize:14, fontWeight:700, color:T.text, margin:0 }}>{MONTHS_LIST[pm.month-1]} {pm.year}</p>
                    <span style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:20,
                      color: stDraft?'#16a34a':'#64748b',
                      background: stDraft?(darkMode?'rgba(22,163,74,0.12)':'#f0fdf4'):(darkMode?'rgba(100,116,139,0.12)':'#f8fafc'),
                      border:`1px solid ${stDraft?(darkMode?'rgba(22,163,74,0.25)':'#bbf7d0'):(darkMode?'rgba(100,116,139,0.25)':'#e2e8f0')}` }}>
                      {stDraft?'Paid':'Draft'}
                    </span>
                  </div>
                  {[
                    { label:'Gross', value:`₹${Number(pm.total_gross||0).toLocaleString('en-IN',{maximumFractionDigits:0})}`, color:T.text2 },
                    { label:'Net',   value:`₹${Number(pm.total_net||0).toLocaleString('en-IN',{maximumFractionDigits:0})}`,   color:T.text, bold:true },
                    { label:'Emps',  value: pm.slip_count, color:T.text3 },
                  ].map(r=>(
                    <div key={r.label} style={{ display:'flex', justifyContent:'space-between', marginTop:4 }}>
                      <span style={{ fontSize:11, color:T.text3 }}>{r.label}</span>
                      <span style={{ fontSize:12, fontWeight: r.bold?700:500, color:r.color }}>{r.value}</span>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* RIGHT — Slips detail */}
      <div style={{ flex:1, minWidth:0 }}>
        {!selectedPM ? (
          <div style={{ background:T.bg2, borderRadius:14, border:`1px solid ${T.cardBorder}`, padding:'80px 0', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="1.5" style={{ marginBottom:14 }}><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
            <p style={{ fontSize:14, fontWeight:600, color:T.text2, margin:0 }}>Select a payroll month</p>
            <p style={{ fontSize:12, color:T.text3, marginTop:5 }}>or run payroll for a new month</p>
          </div>
        ) : (
          <>
            {/* Slip header */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
              <div>
                <h3 style={{ fontSize:15, fontWeight:700, color:T.text, margin:0 }}>{MONTHS_LIST[selectedPM.month-1]} {selectedPM.year} — Salary Register</h3>
                <p style={{ fontSize:12, color:T.text3, marginTop:3 }}>{slips.length} employees · {selectedPM.status}</p>
              </div>
              {selectedPM.status === 'draft' && (
                <button onClick={handleMarkPaid} style={{ display:'flex', alignItems:'center', gap:7, padding:'8px 16px', borderRadius:9, fontSize:13, fontWeight:500, cursor:'pointer', color:'#16a34a', background: darkMode?'rgba(22,163,74,0.1)':'#f0fdf4', border:`1px solid ${darkMode?'rgba(22,163,74,0.25)':'#bbf7d0'}` }}
                  onMouseEnter={e=>e.currentTarget.style.background=darkMode?'rgba(22,163,74,0.2)':'#dcfce7'}
                  onMouseLeave={e=>e.currentTarget.style.background=darkMode?'rgba(22,163,74,0.1)':'#f0fdf4'}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
                  Mark All Paid
                </button>
              )}
            </div>

            {/* Summary cards */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:18 }}>
              {[
                { label:'Total Gross',      value:`₹${Number(selectedPM.total_gross||0).toLocaleString('en-IN',{maximumFractionDigits:0})}`,      color:'#0f1f45', glow:'rgba(15,31,69,0.1)'   },
                { label:'Total Deductions', value:`₹${Number(selectedPM.total_deductions||0).toLocaleString('en-IN',{maximumFractionDigits:0})}`, color:'#dc2626', glow:'rgba(220,38,38,0.1)'  },
                { label:'Net Payable',      value:`₹${Number(selectedPM.total_net||0).toLocaleString('en-IN',{maximumFractionDigits:0})}`,       color:'#16a34a', glow:'rgba(22,163,74,0.1)'  },
              ].map(s => (
                <div key={s.label} style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:14, padding:'14px 18px', boxShadow:`0 4px 12px ${s.glow}`, position:'relative', overflow:'hidden' }}>
                  <div style={{ position:'absolute', top:-12, right:-12, width:40, height:40, borderRadius:'50%', background:s.glow, filter:'blur(8px)' }}/>
                  <p style={{ fontSize:18, fontWeight:800, color:s.color, margin:0 }}>{s.value}</p>
                  <p style={{ fontSize:11, color:T.text3, marginTop:4 }}>{s.label}</p>
                </div>
              ))}
            </div>

            {/* Slips table */}
            <div style={{ background:T.bg2, borderRadius:14, border:`1px solid ${T.cardBorder}`, overflow:'hidden', boxShadow: darkMode?'0 4px 24px rgba(0,0,0,0.3)':'0 2px 12px rgba(15,31,69,0.06)' }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)' }}>
                  <tr>
                    {['Employee','Present','Gross','Deductions','Net Salary','Status','Actions'].map(h=>(
                      <th key={h} style={{ padding:'10px 16px', textAlign:'left', fontSize:11, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {slips.map((slip,i) => {
                    const stPaid = slip.status === 'paid';
                    return (
                      <tr key={slip.id} style={{ borderBottom:`1px solid ${T.border}`, background: darkMode?(i%2===0?T.bg2:T.bg3):(i%2===0?'#fff':'#fafbff'), transition:'background 0.15s' }}
                        onMouseEnter={e=>e.currentTarget.style.background=darkMode?'#1e3a5f':'#f0f4ff'}
                        onMouseLeave={e=>e.currentTarget.style.background=darkMode?(i%2===0?T.bg2:T.bg3):(i%2===0?'#fff':'#fafbff')}>
                        <td style={{ padding:'11px 16px' }}>
                          <p style={{ fontSize:13, fontWeight:600, color:T.text, margin:0 }}>{slip.full_name}</p>
                          <p style={{ fontSize:11, color:T.text3, margin:'2px 0 0' }}>{slip.emp_id} · {slip.department}</p>
                        </td>
                        <td style={{ padding:'11px 16px', fontSize:13, color:T.text2 }}>
                          {slip.present_days} / {slip.working_days}
                          {Number(slip.leave_days)>0 && <p style={{ fontSize:11, color:'#7c3aed', margin:'2px 0 0' }}>{slip.leave_days}d leave</p>}
                          {Number(slip.lop_days)>0 && <p style={{ fontSize:11, color:'#dc2626', margin:'2px 0 0' }}>{slip.lop_days}d LOP</p>}
                        </td>
                        <td style={{ padding:'11px 16px', fontSize:13, fontWeight:500, color:T.text }}>{fc(slip.gross_salary)}</td>
                        <td style={{ padding:'11px 16px' }}>
                          <p style={{ fontSize:13, color:'#dc2626', margin:0 }}>-{fc(slip.total_deductions)}</p>
                          {Number(slip.lop_amount||0)>0 && <p style={{ fontSize:11, color:'#dc2626', margin:'2px 0 0' }}>incl. {fc(slip.lop_amount)} LOP</p>}
                          {Number(slip.bonus||0)>0 && <p style={{ fontSize:11, color:'#16a34a', margin:'2px 0 0' }}>+{fc(slip.bonus)} bonus</p>}
                        </td>
                        <td style={{ padding:'11px 16px' }}>
                          <p style={{ fontSize:14, fontWeight:800, color:T.text, margin:0 }}>{fc(slip.net_salary)}</p>
                          {slip.paid_on && <p style={{ fontSize:11, color:'#16a34a', margin:'2px 0 0' }}>Paid {new Date(slip.paid_on).toLocaleDateString('en-IN',{day:'2-digit',month:'short'})}</p>}
                        </td>
                        <td style={{ padding:'11px 16px' }}>
                          <span style={{ fontSize:11, fontWeight:600, padding:'3px 9px', borderRadius:20,
                            color: stPaid?'#16a34a':'#64748b',
                            background: stPaid?(darkMode?'rgba(22,163,74,0.12)':'#f0fdf4'):(darkMode?'rgba(100,116,139,0.12)':'#f8fafc'),
                            border:`1px solid ${stPaid?(darkMode?'rgba(22,163,74,0.25)':'#bbf7d0'):(darkMode?'rgba(100,116,139,0.25)':'#e2e8f0')}` }}>
                            {stPaid?'Paid':'Draft'}
                          </span>
                        </td>
                        <td style={{ padding:'11px 16px' }}>
                          <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                            <button onClick={() => openEditSlip(slip)} title="Edit"
                              style={{ width:28, height:28, borderRadius:8, border:'none', background:'none', color:T.text3, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}
                              onMouseEnter={e=>{e.currentTarget.style.background=T.bg3; e.currentTarget.style.color=T.text2;}}
                              onMouseLeave={e=>{e.currentTarget.style.background='none'; e.currentTarget.style.color=T.text3;}}>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                            </button>
                            <button onClick={() => handlePrintSlip(slip, selectedPM)} title="Print Payslip"
                              style={{ width:28, height:28, borderRadius:8, border:'none', background:'none', color:'#3b82f6', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}
                              onMouseEnter={e=>e.currentTarget.style.background=darkMode?'rgba(59,130,246,0.12)':'#eff6ff'}
                              onMouseLeave={e=>e.currentTarget.style.background='none'}>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* GENERATE PAYROLL MODAL */}
      {showGenForm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:50, padding:16 }}>
          <div style={{ background:T.bg2, borderRadius:20, width:'100%', maxWidth:460, boxShadow: darkMode?'0 24px 60px rgba(0,0,0,0.6)':'0 24px 60px rgba(15,31,69,0.2)' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'18px 24px', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', borderRadius:'20px 20px 0 0' }}>
              <div>
                <h3 style={{ fontSize:15, fontWeight:700, color:'#fff', margin:0 }}>Run Payroll</h3>
                <p style={{ fontSize:12, color:'rgba(255,255,255,0.6)', margin:'3px 0 0' }}>Generate salary slips based on attendance</p>
              </div>
              <button onClick={()=>setShowGenForm(false)} style={{ width:30, height:30, borderRadius:8, border:'none', background:'rgba(255,255,255,0.1)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div style={{ padding:'20px 24px', background:T.bg3, display:'flex', flexDirection:'column', gap:14 }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div>
                  <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>Month</label>
                  <select value={genForm.month} onChange={e=>setGenForm(f=>({...f,month:Number(e.target.value)}))} style={is}>
                    {MONTHS_LIST.map((m,i)=><option key={i+1} value={i+1}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>Year</label>
                  <select value={genForm.year} onChange={e=>setGenForm(f=>({...f,year:Number(e.target.value)}))} style={is}>
                    {[2024,2025,2026,2027].map(y=><option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ background: darkMode?'rgba(59,130,246,0.08)':'#eff6ff', border:`1px solid ${darkMode?'rgba(59,130,246,0.2)':'#bfdbfe'}`, borderRadius:10, padding:'12px 14px' }}>
                <p style={{ fontSize:12, fontWeight:700, color:'#2563eb', margin:'0 0 6px' }}>What happens when you run payroll:</p>
                <p style={{ fontSize:11, color:'#2563eb', margin:'3px 0' }}>• Attendance for {MONTHS_LIST[genForm.month-1]} {genForm.year} is read automatically (weekly-offs &amp; holidays never counted against pay)</p>
                <p style={{ fontSize:11, color:'#2563eb', margin:'3px 0' }}>• Paid leave within balance is not deducted</p>
                <p style={{ fontSize:11, color:'#2563eb', margin:'3px 0' }}>• Unpaid leave + absence beyond the free monthly quota = Loss of Pay (monthly gross ÷ calendar days)</p>
                <p style={{ fontSize:11, color:'#2563eb', margin:'3px 0' }}>• EPF / ESI / TDS / PT deducted if enabled in salary structure</p>
                <p style={{ fontSize:11, color:'#2563eb', margin:'3px 0' }}>• Running again overwrites the previous draft for the same month</p>
              </div>
            </div>
            <div style={{ display:'flex', justifyContent:'flex-end', gap:10, padding:'14px 24px', borderTop:`1px solid ${T.border}`, background:T.bg2, borderRadius:'0 0 20px 20px' }}>
              <button onClick={()=>setShowGenForm(false)} style={{ padding:'9px 18px', borderRadius:9, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, cursor:'pointer' }}
                onMouseEnter={e=>e.currentTarget.style.background=T.bg3} onMouseLeave={e=>e.currentTarget.style.background=T.bg2}>Cancel</button>
              <button onClick={handleGenerate} disabled={generating} style={{ display:'flex', alignItems:'center', gap:7, padding:'9px 22px', borderRadius:9, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', opacity:generating?0.6:1, boxShadow:'0 4px 12px rgba(15,31,69,0.3)' }}>
                {generating && <span style={{ width:13, height:13, border:'2px solid #fff', borderTopColor:'transparent', borderRadius:'50%', display:'inline-block', animation:'spin 0.6s linear infinite' }}/>}
                {generating ? 'Generating...' : 'Generate Payroll'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT SLIP MODAL */}
      {editSlip && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:50, padding:16 }}>
          <div style={{ background:T.bg2, borderRadius:20, width:'100%', maxWidth:440, boxShadow: darkMode?'0 24px 60px rgba(0,0,0,0.6)':'0 24px 60px rgba(15,31,69,0.2)' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'16px 22px', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', borderRadius:'20px 20px 0 0' }}>
              <div>
                <h3 style={{ fontSize:14, fontWeight:700, color:'#fff', margin:0 }}>Adjust Salary</h3>
                <p style={{ fontSize:12, color:'rgba(255,255,255,0.6)', margin:'3px 0 0' }}>{editSlip.full_name} · {MONTHS_LIST[(selectedPM?.month||1)-1]} {selectedPM?.year}</p>
              </div>
              <button onClick={()=>setEditSlip(null)} style={{ width:30, height:30, borderRadius:8, border:'none', background:'rgba(255,255,255,0.1)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div style={{ padding:'18px 22px', background:T.bg3, display:'flex', flexDirection:'column', gap:14 }}>
              <div style={{ background:T.bg2, border:`1px solid ${T.border}`, borderRadius:10, padding:'10px 14px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span style={{ fontSize:13, color:T.text3 }}>Base Gross</span>
                <span style={{ fontSize:14, fontWeight:700, color:T.text }}>{fc(editSlip.gross_salary)}</span>
              </div>
              {[
                { key:'bonus',            label:'Bonus / Incentive (₹)' },
                { key:'advance_deduction',label:'Advance Recovery (₹)'  },
                { key:'other_deductions', label:'Other Deductions (₹)'  },
              ].map(f=>(
                <div key={f.key}>
                  <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>{f.label}</label>
                  <input type="number" placeholder="0" value={editForm[f.key]} onChange={e=>setEditForm(ef=>({...ef,[f.key]:e.target.value}))} style={is} />
                </div>
              ))}
              <div>
                <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>Notes</label>
                <textarea rows="2" placeholder="Any remarks..." value={editForm.notes} onChange={e=>setEditForm(ef=>({...ef,notes:e.target.value}))} style={{ ...is, resize:'none' }} />
              </div>
            </div>
            <div style={{ display:'flex', justifyContent:'flex-end', gap:10, padding:'14px 22px', borderTop:`1px solid ${T.border}`, background:T.bg2, borderRadius:'0 0 20px 20px' }}>
              <button onClick={()=>setEditSlip(null)} style={{ padding:'8px 16px', borderRadius:9, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, cursor:'pointer' }}
                onMouseEnter={e=>e.currentTarget.style.background=T.bg3} onMouseLeave={e=>e.currentTarget.style.background=T.bg2}>Cancel</button>
              <button onClick={handleSaveSlip} disabled={saving} style={{ display:'flex', alignItems:'center', gap:7, padding:'8px 20px', borderRadius:9, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', opacity:saving?0.6:1 }}>
                {saving && <span style={{ width:13, height:13, border:'2px solid #fff', borderTopColor:'transparent', borderRadius:'50%', display:'inline-block', animation:'spin 0.6s linear infinite' }}/>}
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PayrollComingSoon() {
  return <PlaceholderTab icon="💰" title="Payroll" desc="Monthly payroll run, salary register, and payslip PDF — coming soon." />;
}
function PlaceholderTab({ icon, title, desc }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm py-24 flex flex-col items-center justify-center">
      <div className="text-5xl mb-4">{icon}</div>
      <h3 className="text-lg font-bold text-gray-700 mb-2">{title}</h3>
      <p className="text-sm text-gray-400 max-w-xs text-center">{desc}</p>
    </div>
  );
}

// ── EMPLOYEES TAB ────────────────────────────────────────────────────────────
function EmployeesTab({ token, headers, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', cardBg:'#fff', cardBorder:'#f1f5f9', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const jh = { 'Content-Type':'application/json', ...headers };
  const { showToast, showConfirm } = useToast();

  const [employees,  setEmployees]  = useState([]);
  const [users,      setUsers]      = useState([]);
  const [stats,      setStats]      = useState({});
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [filterDept, setFilterDept] = useState('all');
  const [filterType, setFilterType] = useState('all');

  const [selectedEmp,  setSelectedEmp]  = useState(null);
  const [detailTab,    setDetailTab]    = useState('info');
  const [showForm,     setShowForm]     = useState(false);
  const [editEmp,      setEditEmp]      = useState(null);
  const [showSalary,   setShowSalary]   = useState(false);

  const [form,   setForm]   = useState(EMPTY_FORM);
  const [salary, setSalary] = useState(EMPTY_SALARY);
  const [saving, setSaving] = useState(false);

  const ic = { width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:10, padding:'9px 12px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' };
  const SL = ({ children }) => <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>{children}</label>;

  const avatarColors = ['#0f1f45','#3b82f6','#8b5cf6','#10b981','#f59e0b','#ef4444','#ec4899'];
  const avatarColor  = name => avatarColors[(name?.charCodeAt(0)||0) % avatarColors.length];

  // ── FETCH ──────────────────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [eRes, sRes, uRes] = await Promise.all([
        fetch(`${BASE}/api/employees`, { headers }),
        fetch(`${BASE}/api/employees/stats`, { headers }),
        fetch(`${BASE}/api/employees/unonboarded-users`, { headers }),
      ]);
      const [eData, sData, uData] = await Promise.all([eRes.json(), sRes.json(), uRes.json()]);
      setEmployees(Array.isArray(eData) ? eData : []);
      setStats(sData || {});
      setUsers(Array.isArray(uData) ? uData : []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── FILTER ─────────────────────────────────────────────────────────────────
  const filtered = employees.filter(e => {
    const q = search.toLowerCase();
    const ms = !q || e.full_name?.toLowerCase().includes(q)
      || e.employee_id?.toLowerCase().includes(q)
      || e.designation?.toLowerCase().includes(q)
      || e.department?.toLowerCase().includes(q)
      || e.personal_mobile?.includes(q)
      || e.work_email?.toLowerCase().includes(q);
    const md = filterDept === 'all' || e.department === filterDept;
    const mt = filterType === 'all' || e.employment_type === filterType;
    return ms && md && mt;
  });

  // ── FORM HANDLERS ──────────────────────────────────────────────────────────
  const openAdd = () => {
    setEditEmp(null);
    setForm(EMPTY_FORM);
    setSalary(EMPTY_SALARY);
    setShowForm(true);
  };

  const openEdit = (e) => {
    setEditEmp(e);
    setForm({
      user_id: e.user_id||'', full_name: e.full_name||'',
      personal_email: e.personal_email||'', personal_mobile: e.personal_mobile||'',
      emergency_contact_name: e.emergency_contact_name||'',
      emergency_contact_mobile: e.emergency_contact_mobile||'',
      emergency_contact_relation: e.emergency_contact_relation||'',
      date_of_birth: e.date_of_birth?.split('T')[0]||'',
      gender: e.gender||'', blood_group: e.blood_group||'',
      address: e.address||'', city: e.city||'', state: e.state||'', pincode: e.pincode||'',
      pan_number: e.pan_number||'', aadhaar_number: e.aadhaar_number||'',
      date_of_joining: e.date_of_joining?.split('T')[0]||'',
      department: e.department||'', designation: e.designation||'',
      employment_type: e.employment_type||'full_time',
      reporting_to: e.reporting_to||'',
      work_location: e.work_location||'office',
      status: e.status||'active',
      bank_name: e.bank_name||'', bank_account: e.bank_account||'',
      bank_ifsc: e.bank_ifsc||'', bank_account_type: e.bank_account_type||'',
      notes: e.notes||'', attendance_mode: e.attendance_mode||''
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.full_name.trim()) return showToast('Full name is required', 'error');
    setSaving(true);
    try {
      const url    = editEmp ? `${BASE}/api/employees/${editEmp.id}` : `${BASE}/api/employees`;
      const method = editEmp ? 'PUT' : 'POST';
      const body   = editEmp ? form : { ...form, salary };
      const res    = await fetch(url, { method, headers: jh, body: JSON.stringify(body) });
      const data   = await res.json();
      if (!res.ok) return showToast(data.message || 'Error saving', 'error');
      setShowForm(false);
      showToast(editEmp ? 'Employee updated' : 'Employee onboarded', 'success');
      fetchAll();
      if (selectedEmp?.id === editEmp?.id) setSelectedEmp(data);
    } catch(e) { showToast('Error saving', 'error'); }
    finally { setSaving(false); }
  };

  const refreshSelectedEmp = async () => {
    if (!selectedEmp) return;
    const r = await fetch(`${BASE}/api/employees/${selectedEmp.id}`, { headers });
    if (r.ok) setSelectedEmp(await r.json());
  };

  const [editBal, setEditBal] = useState(null);   // { id, value }
  const [balBusy, setBalBusy] = useState(false);

  const saveBalance = async () => {
    if (!editBal) return;
    setBalBusy(true);
    try {
      const res = await fetch(`${BASE}/api/employees/leaves/balances/${editBal.id}`, {
        method:'PUT', headers:jh, body:JSON.stringify({ total_days: Number(editBal.value) })
      });
      if (!res.ok) return showToast('Error saving balance', 'error');
      setEditBal(null);
      showToast('Balance updated', 'success');
      await refreshSelectedEmp();
    } catch(e) { showToast('Error saving balance', 'error'); }
    finally { setBalBusy(false); }
  };

  const recalcBalances = async () => {
    if (!selectedEmp) return;
    setBalBusy(true);
    try {
      const res = await fetch(`${BASE}/api/employees/leaves/balances/employee/${selectedEmp.id}/recalc`, {
        method:'POST', headers:jh, body: JSON.stringify({ clear_override: true })
      });
      if (!res.ok) return showToast('Error recalculating', 'error');
      showToast('Balances recalculated from join date', 'success');
      await refreshSelectedEmp();
    } catch(e) { showToast('Error', 'error'); }
    finally { setBalBusy(false); }
  };

  const handleSaveSalary = async () => {
    if (!selectedEmp) return;
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/api/employees/${selectedEmp.id}/salary`, {
        method: 'PUT', headers: jh, body: JSON.stringify(salary)
      });
      if (res.ok) {
        setShowSalary(false);
        const updated = await fetch(`${BASE}/api/employees/${selectedEmp.id}`, { headers });
        const data = await updated.json();
        setSelectedEmp(data);
        fetchAll();
      }
      if (!res.ok) showToast('Error saving salary', 'error'); else showToast('Salary structure saved', 'success');
    } catch(e) { showToast('Error saving salary', 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    const ok = await showConfirm(
      'Offboard this employee? This permanently removes their HR records, attendance and payroll history.',
      { title:'Offboard Employee', confirmLabel:'Offboard', confirmColor:'#dc2626' }
    );
    if (!ok) return;
    await fetch(`${BASE}/api/employees/${id}`, { method:'DELETE', headers });
    showToast('Employee offboarded', 'success');
    if (selectedEmp?.id === id) setSelectedEmp(null);
    fetchAll();
  };

  const openSalary = (e) => {
    setSelectedEmp(e);
    setDetailTab('salary');
    const ss = e.salary_structure;
    if (ss) setSalary({ ...ss });
    else setSalary({ ...EMPTY_SALARY });
    setShowSalary(true);
  };

  const grossSalary = () =>
    Number(salary.basic||0) + Number(salary.hra||0) +
    Number(salary.travel_allowance||0) + Number(salary.special_allowance||0) +
    Number(salary.other_allowance||0);

  const totalDeductions = () => {
    let d = 0;
    if (salary.enable_epf) d += grossSalary() * (Number(salary.epf_employee_percent||12)/100);
    if (salary.enable_esi) d += grossSalary() * (Number(salary.esi_employee_percent||0.75)/100);
    if (salary.enable_tds) d += Number(salary.tds_monthly||0);
    if (salary.enable_pt)  d += Number(salary.pt_monthly||0);
    return d;
  };


  const statusBadge = (status) => {
    const s = {
      active:     { c:'#16a34a', bg: darkMode?'rgba(22,163,74,0.12)':'#f0fdf4',   border: darkMode?'rgba(22,163,74,0.25)':'#bbf7d0',   label:'Active'     },
      on_leave:   { c:'#d97706', bg: darkMode?'rgba(245,158,11,0.12)':'#fffbeb',  border: darkMode?'rgba(245,158,11,0.25)':'#fde68a',   label:'On Leave'   },
      terminated: { c:'#dc2626', bg: darkMode?'rgba(220,38,38,0.12)':'#fef2f2',   border: darkMode?'rgba(220,38,38,0.25)':'#fecaca',    label:'Terminated' },
      resigned:   { c:'#64748b', bg: darkMode?'rgba(100,116,139,0.12)':'#f8fafc', border: darkMode?'rgba(100,116,139,0.25)':'#e2e8f0',  label:'Resigned'   },
      probation:  { c:'#2563eb', bg: darkMode?'rgba(37,99,235,0.12)':'#eff6ff',   border: darkMode?'rgba(37,99,235,0.25)':'#bfdbfe',    label:'Probation'  },
    };
    return s[status] || s.active;
  };

  const deptBadge = (dept) => {
    const colors = {
      'GST & Indirect Tax': { c:'#7c3aed', bg: darkMode?'rgba(124,58,237,0.12)':'#f5f3ff' },
      'Income Tax & ITR':   { c:'#2563eb', bg: darkMode?'rgba(37,99,235,0.12)':'#eff6ff'  },
      'MCA & Company Law':  { c:'#4338ca', bg: darkMode?'rgba(67,56,202,0.12)':'#eef2ff'  },
      'Accounts & Audit':   { c:'#0d9488', bg: darkMode?'rgba(13,148,136,0.12)':'#f0fdfa' },
      'TDS & Compliance':   { c:'#ea580c', bg: darkMode?'rgba(234,88,12,0.12)':'#fff7ed'  },
      'Administration':     { c:'#db2777', bg: darkMode?'rgba(219,39,119,0.12)':'#fdf2f8' },
      'Management':         { c:'#64748b', bg: darkMode?'rgba(100,116,139,0.12)':'#f8fafc' },
    };
    return colors[dept] || { c:'#64748b', bg: darkMode?'rgba(100,116,139,0.12)':'#f8fafc' };
  };

  // ── RENDER ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ display:'flex', gap:20 }}>

      {/* LEFT: LIST */}
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:18 }}>
          <div>
            <h2 style={{ fontSize:15, fontWeight:700, color:T.text, margin:0 }}>Employees</h2>
            <p style={{ fontSize:12, color:T.text3, marginTop:3 }}>{stats.active||0} active · {stats.total||0} total</p>
          </div>
          <button onClick={openAdd} style={{ display:'flex', alignItems:'center', gap:7, padding:'9px 18px', borderRadius:10, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', boxShadow:'0 4px 12px rgba(15,31,69,0.3)' }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Onboard Employee
          </button>
        </div>

        {/* Stats */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:18 }}>
          {[
            { label:'Total',    value: stats.total||0,    color:'#3b82f6', glow:'rgba(59,130,246,0.12)'  },
            { label:'Active',   value: stats.active||0,   color:'#10b981', glow:'rgba(16,185,129,0.12)'  },
            { label:'Present',  value: stats.present||0,  color:'#6366f1', glow:'rgba(99,102,241,0.12)'  },
            { label:'On Leave', value: stats.on_leave||0, color:'#f59e0b', glow:'rgba(245,158,11,0.12)'  },
          ].map(s => (
            <div key={s.label} style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:14, padding:'14px 18px', boxShadow:`0 4px 12px ${s.glow}`, position:'relative', overflow:'hidden' }}>
              <div style={{ position:'absolute', top:-14, right:-14, width:44, height:44, borderRadius:'50%', background:s.glow, filter:'blur(10px)' }}/>
              <p style={{ fontSize:22, fontWeight:800, color:s.color, margin:0 }}>{s.value}</p>
              <p style={{ fontSize:11, color:T.text3, marginTop:4, fontWeight:500 }}>{s.label}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={{ background:T.bg2, border:`1px solid ${T.border}`, borderRadius:14, padding:'12px 16px', display:'flex', alignItems:'center', gap:10, marginBottom:18 }}>
          <div style={{ position:'relative', flex:1 }}>
            <svg style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)' }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input type="text" placeholder="Search name, ID, designation, department..."
              value={search} onChange={e=>setSearch(e.target.value)}
              style={{ ...ic, paddingLeft:34 }} />
          </div>
          <select value={filterDept} onChange={e=>setFilterDept(e.target.value)}
            style={{ border:`1px solid ${T.inputBorder}`, borderRadius:10, padding:'9px 12px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, minWidth:170 }}>
            <option value="all">All Departments</option>
            {DEPARTMENTS.map(d=><option key={d} value={d}>{d}</option>)}
          </select>
          <select value={filterType} onChange={e=>setFilterType(e.target.value)}
            style={{ border:`1px solid ${T.inputBorder}`, borderRadius:10, padding:'9px 12px', fontSize:13, outline:'none', background:T.inputBg, color:T.text }}>
            <option value="all">All Types</option>
            {EMPLOYMENT_TYPES.map(t=><option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>

        {/* Table */}
        <div style={{ background:T.bg2, borderRadius:14, border:`1px solid ${T.cardBorder}`, overflow:'hidden', boxShadow: darkMode?'0 4px 24px rgba(0,0,0,0.3)':'0 2px 12px rgba(15,31,69,0.06)' }}>
          {loading ? (
            <div style={{ padding:'64px 0', textAlign:'center', color:T.text3, fontSize:13 }}>Loading employees...</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding:'64px 0', textAlign:'center' }}>
              <div style={{ width:60, height:60, background:T.bg3, borderRadius:16, border:`2px dashed ${T.border2}`, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px' }}>
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="1.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
              </div>
              <p style={{ fontSize:14, fontWeight:600, color:T.text2, margin:0 }}>No employees found</p>
              <p style={{ fontSize:12, color:T.text3, marginTop:5 }}>{employees.length===0?'Click "Onboard Employee" to add your first team member':'Try adjusting your filters'}</p>
            </div>
          ) : (
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)' }}>
                <tr>
                  {['Employee','Department','Type','Joining Date','Contact','CTC','Status',''].map(h=>(
                    <th key={h} style={{ padding:'11px 16px', textAlign:'left', fontSize:11, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em', whiteSpace:'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((e,i) => {
                  const st  = statusBadge(e.status);
                  const dep = deptBadge(e.department);
                  const emp = EMPLOYMENT_TYPES.find(t=>t.value===e.employment_type);
                  const isSel = selectedEmp?.id === e.id;
                  return (
                    <tr key={e.id} onClick={() => { setSelectedEmp(e); setDetailTab('info'); }}
                      style={{ borderBottom:`1px solid ${T.border}`, cursor:'pointer', transition:'background 0.15s',
                        background: isSel?(darkMode?'rgba(59,130,246,0.12)':'#eff6ff'):(darkMode&&i%2!==0?T.bg3:'transparent') }}
                      onMouseEnter={ev => { if(!isSel) ev.currentTarget.style.background=darkMode?'#1e3a5f':'#f0f4ff'; }}
                      onMouseLeave={ev => { ev.currentTarget.style.background=isSel?(darkMode?'rgba(59,130,246,0.12)':'#eff6ff'):(darkMode&&i%2!==0?T.bg3:'transparent'); }}>
                      <td style={{ padding:'12px 16px' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                          <div style={{ width:36, height:36, borderRadius:'50%', background: avatarColor(e.full_name), display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:14, fontWeight:700, flexShrink:0 }}>
                            {e.full_name?.charAt(0)?.toUpperCase()}
                          </div>
                          <div>
                            <p style={{ fontSize:13, fontWeight:600, color:T.text, margin:0 }}>{e.full_name}</p>
                            <p style={{ fontSize:11, color:T.text3, fontFamily:'monospace', margin:'2px 0 0' }}>{e.employee_id}</p>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding:'12px 16px' }}>
                        {e.department ? <span style={{ fontSize:11, fontWeight:600, padding:'3px 8px', borderRadius:20, color:dep.c, background:dep.bg }}>{e.department}</span> : <span style={{ fontSize:11, color:T.text3 }}>—</span>}
                      </td>
                      <td style={{ padding:'12px 16px', fontSize:12, color:T.text3 }}>{emp?.label||e.employment_type}</td>
                      <td style={{ padding:'12px 16px', fontSize:12, color:T.text3, whiteSpace:'nowrap' }}>
                        {e.date_of_joining ? new Date(e.date_of_joining).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '—'}
                      </td>
                      <td style={{ padding:'12px 16px' }}>
                        <p style={{ fontSize:12, color:T.text2, margin:0 }}>{e.personal_mobile||'—'}</p>
                        <p style={{ fontSize:11, color:T.text3, margin:'2px 0 0', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:144 }}>{e.work_email||''}</p>
                      </td>
                      <td style={{ padding:'12px 16px', fontSize:13, fontWeight:600, color:T.text, whiteSpace:'nowrap' }}>
                        {e.ctc ? fc(e.ctc) : <span style={{ fontSize:11, color:T.text3 }}>Not set</span>}
                      </td>
                      <td style={{ padding:'12px 16px' }}>
                        <span style={{ fontSize:11, fontWeight:600, padding:'3px 9px', borderRadius:20, color:st.c, background:st.bg, border:`1px solid ${st.border}` }}>{st.label}</span>
                      </td>
                      <td style={{ padding:'12px 16px' }} onClick={ev=>ev.stopPropagation()}>
                        <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                          <button onClick={()=>openEdit(e)}
                            style={{ width:28, height:28, borderRadius:8, border:'none', background:'none', color:T.text3, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}
                            onMouseEnter={e2=>{e2.currentTarget.style.background=T.bg3;e2.currentTarget.style.color=T.text2;}}
                            onMouseLeave={e2=>{e2.currentTarget.style.background='none';e2.currentTarget.style.color=T.text3;}}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          </button>
                          <button onClick={()=>handleDelete(e.id)}
                            style={{ width:28, height:28, borderRadius:8, border:'none', background:'none', color:'#ef4444', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}
                            onMouseEnter={e2=>e2.currentTarget.style.background=darkMode?'rgba(239,68,68,0.12)':'#fef2f2'}
                            onMouseLeave={e2=>e2.currentTarget.style.background='none'}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* RIGHT: DETAIL PANEL */}
      {selectedEmp && (
        <div style={{ width:360, background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:16, display:'flex', flexDirection:'column', flexShrink:0, overflow:'hidden', maxHeight:'calc(100vh - 140px)', position:'sticky', top:24, boxShadow: darkMode?'0 8px 32px rgba(0,0,0,0.4)':'0 4px 20px rgba(15,31,69,0.1)' }}>
          <div style={{ padding:'16px 20px', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexShrink:0 }}>
            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
              <div style={{ width:44, height:44, borderRadius:'50%', background:'rgba(255,255,255,0.15)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:18, fontWeight:700, flexShrink:0 }}>
                {selectedEmp.full_name?.charAt(0)?.toUpperCase()}
              </div>
              <div>
                <p style={{ fontSize:14, fontWeight:700, color:'#fff', margin:0 }}>{selectedEmp.full_name}</p>
                <p style={{ fontSize:11, color:'rgba(255,255,255,0.6)', fontFamily:'monospace', margin:'3px 0 0' }}>{selectedEmp.employee_id}</p>
                {selectedEmp.designation && <p style={{ fontSize:11, color:'rgba(255,255,255,0.5)', margin:'2px 0 0' }}>{selectedEmp.designation}</p>}
              </div>
            </div>
            <button onClick={()=>setSelectedEmp(null)}
              style={{ width:30, height:30, borderRadius:8, border:'none', background:'rgba(255,255,255,0.1)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}
              onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.2)'}
              onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,0.1)'}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>

          <div style={{ display:'flex', borderBottom:`1px solid ${T.border}`, flexShrink:0, background:T.bg2 }}>
            {[{key:'info',label:'Info'},{key:'contact',label:'Contact'},{key:'salary',label:'Salary'},{key:'leaves',label:'Leaves'}].map(t=>(
              <button key={t.key} onClick={()=>setDetailTab(t.key)} style={{
                flex:1, padding:'10px 0', fontSize:12, fontWeight:600, border:'none', cursor:'pointer',
                borderBottom: detailTab===t.key?'2px solid #3b82f6':'2px solid transparent',
                color: detailTab===t.key?'#3b82f6':T.text3, background:'none',
              }}
              onMouseEnter={e=>{ if(detailTab!==t.key) e.currentTarget.style.color=T.text2; }}
              onMouseLeave={e=>{ if(detailTab!==t.key) e.currentTarget.style.color=T.text3; }}>
                {t.label}
              </button>
            ))}
          </div>

          <div style={{ flex:1, overflowY:'auto', padding:18, background:T.bg2 }}>
            {detailTab==='info' && (
              <div>
                {[
                  { label:'Department',    value: selectedEmp.department },
                  { label:'Designation',   value: selectedEmp.designation },
                  { label:'Employment',    value: EMPLOYMENT_TYPES.find(t=>t.value===selectedEmp.employment_type)?.label },
                  { label:'Work Location', value: selectedEmp.work_location },
                  { label:'Reporting To',  value: selectedEmp.reporting_to_name },
                  { label:'Date of Joining', value: selectedEmp.date_of_joining ? new Date(selectedEmp.date_of_joining).toLocaleDateString('en-IN',{day:'2-digit',month:'long',year:'numeric'}) : null },
                  { label:'Gender',        value: selectedEmp.gender },
                  { label:'Blood Group',   value: selectedEmp.blood_group },
                  { label:'Date of Birth', value: selectedEmp.date_of_birth ? new Date(selectedEmp.date_of_birth).toLocaleDateString('en-IN',{day:'2-digit',month:'long',year:'numeric'}) : null },
                  { label:'PAN',           value: selectedEmp.pan_number, mono:true },
                  { label:'Aadhaar',       value: selectedEmp.aadhaar_number, mono:true },
                ].filter(r=>r.value).map(r=>(
                  <div key={r.label} style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', padding:'9px 0', borderBottom:`1px solid ${T.border}` }}>
                    <span style={{ fontSize:11, color:T.text3, width:110, flexShrink:0 }}>{r.label}</span>
                    <span style={{ fontSize:13, color:T.text, fontWeight:500, textAlign:'right', fontFamily:r.mono?'monospace':'inherit' }}>{r.value}</span>
                  </div>
                ))}
                {selectedEmp.status && (()=>{ const st=statusBadge(selectedEmp.status); return (
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'9px 0' }}>
                    <span style={{ fontSize:11, color:T.text3 }}>Status</span>
                    <span style={{ fontSize:11, fontWeight:600, padding:'3px 9px', borderRadius:20, color:st.c, background:st.bg, border:`1px solid ${st.border}` }}>{st.label}</span>
                  </div>
                );})()}
                {selectedEmp.notes && (
                  <div style={{ marginTop:12, padding:12, background:T.bg3, borderRadius:10, border:`1px solid ${T.border}` }}>
                    <p style={{ fontSize:11, color:T.text3, fontWeight:600, marginBottom:6 }}>Notes</p>
                    <p style={{ fontSize:13, color:T.text2, lineHeight:1.5, margin:0 }}>{selectedEmp.notes}</p>
                  </div>
                )}
              </div>
            )}

            {detailTab==='contact' && (
              <div>
                {[
                  { label:'Work Email',      value: selectedEmp.work_email },
                  { label:'Personal Mobile', value: selectedEmp.personal_mobile },
                  { label:'Personal Email',  value: selectedEmp.personal_email },
                ].filter(r=>r.value).map(r=>(
                  <div key={r.label} style={{ display:'flex', justifyContent:'space-between', padding:'9px 0', borderBottom:`1px solid ${T.border}` }}>
                    <span style={{ fontSize:11, color:T.text3, width:130, flexShrink:0 }}>{r.label}</span>
                    <span style={{ fontSize:13, color:T.text, fontWeight:500, textAlign:'right', wordBreak:'break-all' }}>{r.value}</span>
                  </div>
                ))}
                {selectedEmp.address && (
                  <div style={{ marginTop:12 }}>
                    <p style={{ fontSize:11, color:T.text3, fontWeight:600, marginBottom:6 }}>Address</p>
                    <p style={{ fontSize:13, color:T.text2, background:T.bg3, padding:12, borderRadius:10, border:`1px solid ${T.border}`, lineHeight:1.6, margin:0 }}>
                      {selectedEmp.address}{selectedEmp.city?`, ${selectedEmp.city}`:''}{selectedEmp.state?`, ${selectedEmp.state}`:''}{selectedEmp.pincode?` — ${selectedEmp.pincode}`:''}
                    </p>
                  </div>
                )}
                {selectedEmp.emergency_contact_name && (
                  <div style={{ marginTop:14, background: darkMode?'rgba(239,68,68,0.08)':'#fef2f2', border:`1px solid ${darkMode?'rgba(239,68,68,0.2)':'#fecaca'}`, borderRadius:12, padding:14 }}>
                    <p style={{ fontSize:10, fontWeight:700, color:'#dc2626', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:8 }}>Emergency Contact</p>
                    <p style={{ fontSize:14, fontWeight:600, color:T.text, margin:0 }}>{selectedEmp.emergency_contact_name}</p>
                    {selectedEmp.emergency_contact_relation && <p style={{ fontSize:12, color:T.text3, marginTop:3 }}>{selectedEmp.emergency_contact_relation}</p>}
                    {selectedEmp.emergency_contact_mobile && <p style={{ fontSize:13, fontFamily:'monospace', color:T.text2, marginTop:5 }}>{selectedEmp.emergency_contact_mobile}</p>}
                  </div>
                )}
                {selectedEmp.bank_account && (
                  <div style={{ marginTop:14, background:T.bg3, border:`1px solid ${T.border}`, borderRadius:12, padding:14 }}>
                    <p style={{ fontSize:10, fontWeight:700, color:T.text3, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:8 }}>Bank Account</p>
                    {[
                      { label:'Bank',    value: selectedEmp.bank_name },
                      { label:'Account', value: selectedEmp.bank_account, mono:true },
                      { label:'IFSC',    value: selectedEmp.bank_ifsc, mono:true },
                      { label:'Type',    value: selectedEmp.bank_account_type },
                    ].filter(r=>r.value).map(r=>(
                      <div key={r.label} style={{ display:'flex', justifyContent:'space-between', padding:'4px 0' }}>
                        <span style={{ fontSize:11, color:T.text3 }}>{r.label}</span>
                        <span style={{ fontSize:13, fontWeight:500, color:T.text, fontFamily:r.mono?'monospace':'inherit' }}>{r.value}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {detailTab==='salary' && (
              <div>
                {selectedEmp.salary_structure ? (
                  <>
                    <div style={{ background: darkMode?'rgba(59,130,246,0.1)':'#eff6ff', border:`1px solid ${darkMode?'rgba(59,130,246,0.2)':'#bfdbfe'}`, borderRadius:14, padding:16, marginBottom:14 }}>
                      <p style={{ fontSize:11, fontWeight:700, color:'#2563eb', textTransform:'uppercase', letterSpacing:'0.06em', margin:'0 0 4px' }}>Monthly CTC</p>
                      <p style={{ fontSize:28, fontWeight:800, color:T.text, margin:0 }}>{fc(selectedEmp.salary_structure.ctc)}</p>
                    </div>
                    {[
                      { label:'Basic Pay',        value: selectedEmp.salary_structure.basic },
                      { label:'HRA',              value: selectedEmp.salary_structure.hra },
                      { label:'Travel Allowance', value: selectedEmp.salary_structure.travel_allowance },
                      { label:'Special Allowance',value: selectedEmp.salary_structure.special_allowance },
                      { label:'Other Allowance',  value: selectedEmp.salary_structure.other_allowance },
                    ].filter(r=>Number(r.value)>0).map(r=>(
                      <div key={r.label} style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:`1px solid ${T.border}` }}>
                        <span style={{ fontSize:12, color:T.text3 }}>{r.label}</span>
                        <span style={{ fontSize:13, fontWeight:500, color:T.text }}>{fc(r.value)}</span>
                      </div>
                    ))}
                    <div style={{ display:'flex', flexDirection:'column', gap:6, marginTop:14 }}>
                      {[{key:'enable_epf',label:'EPF'},{key:'enable_esi',label:'ESI'},{key:'enable_tds',label:'TDS'},{key:'enable_pt',label:'Prof. Tax'}].map(d=>(
                        <div key={d.key} style={{ display:'flex', justifyContent:'space-between', padding:'8px 12px', borderRadius:9,
                          background: selectedEmp.salary_structure[d.key]?(darkMode?'rgba(16,185,129,0.1)':'#f0fdf4'):(darkMode?T.bg3:'#f8fafc'),
                          border:`1px solid ${selectedEmp.salary_structure[d.key]?(darkMode?'rgba(16,185,129,0.2)':'#bbf7d0'):T.border}` }}>
                          <span style={{ fontSize:12, color:T.text2 }}>{d.label}</span>
                          <span style={{ fontSize:12, fontWeight:700, color: selectedEmp.salary_structure[d.key]?'#16a34a':T.text3 }}>{selectedEmp.salary_structure[d.key]?'ON':'OFF'}</span>
                        </div>
                      ))}
                    </div>
                    <button onClick={() => openSalary(selectedEmp)} style={{ width:'100%', marginTop:14, padding:'10px 0', borderRadius:10, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}
                      onMouseEnter={e=>e.currentTarget.style.background=T.bg3}
                      onMouseLeave={e=>e.currentTarget.style.background=T.bg2}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                      Edit Salary Structure
                    </button>
                  </>
                ) : (
                  <div style={{ padding:'48px 0', textAlign:'center' }}>
                    <p style={{ fontSize:13, fontWeight:600, color:T.text2, margin:0 }}>No salary structure set</p>
                    <button onClick={() => openSalary(selectedEmp)} style={{ marginTop:12, padding:'8px 18px', borderRadius:9, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                      Set Salary Structure
                    </button>
                  </div>
                )}
              </div>
            )}

            {detailTab==='leaves' && (
              <div>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
                  <p style={{ fontSize:11, color:T.text3, margin:0 }}>
                    Joined {selectedEmp.date_of_joining ? new Date(selectedEmp.date_of_joining).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '—'}
                    {' · '}quotas pro-rated for the joining year
                  </p>
                  <button onClick={recalcBalances} disabled={balBusy} style={{ fontSize:11.5, fontWeight:600, padding:'6px 12px', borderRadius:8, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, cursor:'pointer', opacity:balBusy?0.6:1 }}>
                    {balBusy ? 'Working…' : '↻ Recalculate'}
                  </button>
                </div>
                {(!selectedEmp.leave_balances || selectedEmp.leave_balances.length===0) ? (
                  <p style={{ fontSize:13, color:T.text3, textAlign:'center', padding:'32px 0' }}>
                    No leave balances yet — use Recalculate, or Rebuild Balances in Settings → HR &amp; Payroll.
                  </p>
                ) : selectedEmp.leave_balances.map(lb => {
                  const pct = lb.total_days > 0 ? (lb.used_days/lb.total_days)*100 : 0;
                  const isEditing = editBal?.id === lb.id;
                  return (
                    <div key={lb.id} style={{ background:T.bg3, border:`1px solid ${T.border}`, borderRadius:12, padding:14, marginBottom:10 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:10 }}>
                        <div>
                          <p style={{ fontSize:13, fontWeight:600, color:T.text, margin:0 }}>
                            {lb.leave_type_name}
                            {lb.manual_override && <span style={{ fontSize:9, fontWeight:700, color:'#d97706', background:'rgba(217,119,6,0.12)', padding:'1px 6px', borderRadius:6, marginLeft:6 }}>ADJUSTED</span>}
                            {lb.pro_rated && !lb.manual_override && <span style={{ fontSize:9, fontWeight:700, color:'#2563eb', background:'rgba(37,99,235,0.12)', padding:'1px 6px', borderRadius:6, marginLeft:6 }}>PRO-RATED</span>}
                          </p>
                          <p style={{ fontSize:11, color:T.text3, fontFamily:'monospace', margin:'2px 0 0' }}>{lb.code}</p>
                        </div>
                        <div style={{ textAlign:'right' }}>
                          <p style={{ fontSize:20, fontWeight:800, color:T.text, margin:0 }}>{lb.balance_days ?? (lb.total_days - lb.used_days)}</p>
                          <p style={{ fontSize:11, color:T.text3, margin:0 }}>days left</p>
                        </div>
                      </div>
                      <div style={{ width:'100%', background: darkMode?'rgba(255,255,255,0.08)':'#e5e7eb', borderRadius:20, height:6 }}>
                        <div style={{ height:6, borderRadius:20, background:'#3b82f6', width:`${Math.min(pct,100)}%` }}/>
                      </div>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:6 }}>
                        <span style={{ fontSize:11, color:T.text3 }}>{lb.used_days||0} used · {lb.pending_days||0} pending</span>
                        {isEditing ? (
                          <span style={{ display:'flex', alignItems:'center', gap:6 }}>
                            <input type="number" min={0} step="0.5" value={editBal.value} autoFocus
                              onChange={e=>setEditBal(b=>({...b, value:e.target.value}))}
                              style={{ width:64, border:`1px solid ${T.inputBorder}`, borderRadius:7, padding:'3px 6px', fontSize:12, background:T.inputBg, color:T.text }} />
                            <button onClick={saveBalance} disabled={balBusy} style={{ fontSize:11, fontWeight:700, color:'#16a34a', background:'none', border:'none', cursor:'pointer' }}>Save</button>
                            <button onClick={()=>setEditBal(null)} style={{ fontSize:11, color:T.text3, background:'none', border:'none', cursor:'pointer' }}>Cancel</button>
                          </span>
                        ) : (
                          <span style={{ display:'flex', alignItems:'center', gap:8 }}>
                            <span style={{ fontSize:11, color:T.text3 }}>{lb.total_days} total</span>
                            <button onClick={()=>setEditBal({ id:lb.id, value:String(lb.total_days) })}
                              style={{ fontSize:11, fontWeight:600, color:'#3b82f6', background:'none', border:'none', cursor:'pointer' }}>Adjust</button>
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div style={{ padding:'12px 16px', borderTop:`1px solid ${T.border}`, display:'flex', gap:8, flexShrink:0, background:T.bg2 }}>
            <button onClick={() => openEdit(selectedEmp)} style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:7, padding:'9px 0', borderRadius:10, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, cursor:'pointer' }}
              onMouseEnter={e=>e.currentTarget.style.background=T.bg3}
              onMouseLeave={e=>e.currentTarget.style.background=T.bg2}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              Edit
            </button>
            <button onClick={() => handleDelete(selectedEmp.id)} style={{ padding:'9px 14px', borderRadius:10, border:`1px solid ${darkMode?'rgba(239,68,68,0.25)':'#fecaca'}`, background: darkMode?'rgba(239,68,68,0.08)':'#fef2f2', color:'#ef4444', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}
              onMouseEnter={e=>e.currentTarget.style.background=darkMode?'rgba(239,68,68,0.15)':'#fee2e2'}
              onMouseLeave={e=>e.currentTarget.style.background=darkMode?'rgba(239,68,68,0.08)':'#fef2f2'}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
            </button>
          </div>
        </div>
      )}

      {/* ONBOARD / EDIT MODAL */}
      {showForm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:50, padding:16 }}>
          <div style={{ background:T.bg2, borderRadius:20, width:'100%', maxWidth:960, height:'92vh', display:'flex', flexDirection:'column', boxShadow: darkMode?'0 24px 60px rgba(0,0,0,0.6)':'0 24px 60px rgba(15,31,69,0.2)' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'18px 32px', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', borderRadius:'20px 20px 0 0', flexShrink:0 }}>
              <div style={{ display:'flex', alignItems:'center', gap:14 }}>
                <div style={{ width:42, height:42, borderRadius:11, background:'rgba(255,255,255,0.1)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
                </div>
                <div>
                  <h3 style={{ fontSize:15, fontWeight:700, color:'#fff', margin:0 }}>{editEmp ? 'Edit Employee' : 'Onboard New Employee'}</h3>
                  <p style={{ fontSize:12, color:'rgba(255,255,255,0.6)', margin:'3px 0 0' }}>{editEmp ? `Employee ID: ${editEmp.employee_id}` : 'Create a complete HR record for your team member'}</p>
                </div>
              </div>
              <button onClick={() => setShowForm(false)}
                style={{ width:34, height:34, borderRadius:9, border:'none', background:'rgba(255,255,255,0.1)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}
                onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.2)'}
                onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,0.1)'}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            <div style={{ flex:1, overflowY:'auto', padding:'8px 32px 24px', background:T.bg3, scrollbarWidth:'none' }}>

              {/* Sections rendered from config */}
              {[
                ...(!editEmp ? [{
                  num:'1', title:'Link System User',
                  content: (
                    <div>
                      <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>Link to System User (optional)</label>
                      <select value={form.user_id} onChange={e => {
                        const uid = e.target.value;
                        const user = users.find(u => String(u.id) === uid);
                        setForm(f => ({ ...f, user_id: uid, full_name: user?(f.full_name||user.full_name):f.full_name, personal_email: user?(f.personal_email||user.email):f.personal_email }));
                      }} style={ic}>
                        <option value="">— Select a user (or leave blank for external) —</option>
                        {users.map(u=><option key={u.id} value={u.id}>{u.full_name} ({u.email}) — {u.role}</option>)}
                      </select>
                      <p style={{ fontSize:11, color:T.text3, marginTop:5 }}>Linking enables auto attendance punch on login</p>
                    </div>
                  )
                }] : []),
                {
                  num: editEmp?'1':'2', title:'Personal Information',
                  content: (
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:14 }}>
                      <div style={{ gridColumn:'span 2' }}>
                        <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>Full Name *</label>
                        <input type="text" placeholder="As per PAN Card" value={form.full_name} onChange={e=>setForm(f=>({...f,full_name:e.target.value}))} style={ic} />
                      </div>
                      {[
                        { label:'Gender', content: <select value={form.gender} onChange={e=>setForm(f=>({...f,gender:e.target.value}))} style={ic}><option value="">Select...</option><option>Male</option><option>Female</option><option>Other</option></select> },
                        { label:'Date of Birth', content: <SmartDatePicker value={form.date_of_birth} onChange={v=>setForm(f=>({...f,date_of_birth:v}))} placeholder="Select DOB" darkMode={darkMode} T={T} /> },
                        { label:'Blood Group', content: <select value={form.blood_group} onChange={e=>setForm(f=>({...f,blood_group:e.target.value}))} style={ic}><option value="">Select...</option>{BLOOD_GROUPS.map(g=><option key={g}>{g}</option>)}</select> },
                        { label:'PAN Number', content: <input type="text" placeholder="ABCDE1234F" maxLength={10} value={form.pan_number} onChange={e=>setForm(f=>({...f,pan_number:e.target.value.toUpperCase()}))} style={ic} /> },
                        { label:'Aadhaar', content: <input type="text" placeholder="123456789012" maxLength={12} value={form.aadhaar_number} onChange={e=>setForm(f=>({...f,aadhaar_number:e.target.value.replace(/\D/g,'')}))} style={ic} /> },
                      ].map(f=>(
                        <div key={f.label}>
                          <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>{f.label}</label>
                          {f.content}
                        </div>
                      ))}
                      <div style={{ gridColumn:'span 2' }}>
                        <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>Address</label>
                        <textarea rows="2" placeholder="Residential address..." value={form.address} onChange={e=>setForm(f=>({...f,address:e.target.value}))} style={{ ...ic, resize:'none' }} />
                      </div>
                      <div>
                        <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>City</label>
                        <input type="text" placeholder="Lucknow" value={form.city} onChange={e=>setForm(f=>({...f,city:e.target.value}))} style={ic} />
                      </div>
                      <div>
                        <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>State</label>
                        <input type="text" placeholder="Uttar Pradesh" value={form.state} onChange={e=>setForm(f=>({...f,state:e.target.value}))} style={ic} />
                      </div>
                      <div>
                        <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>Pincode</label>
                        <input type="text" placeholder="226001" maxLength={6} value={form.pincode} onChange={e=>setForm(f=>({...f,pincode:e.target.value.replace(/\D/g,'')}))} style={ic} />
                      </div>
                    </div>
                  )
                },
                {
                  num: editEmp?'2':'3', title:'Contact Information',
                  content: (
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:14 }}>
                      <div>
                        <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>Personal Mobile</label>
                        <input type="text" placeholder="9876543210" maxLength={15} value={form.personal_mobile} onChange={e=>setForm(f=>({...f,personal_mobile:e.target.value}))} style={ic} />
                      </div>
                      <div style={{ gridColumn:'span 2' }}>
                        <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>Personal Email</label>
                        <input type="email" placeholder="personal@gmail.com" value={form.personal_email} onChange={e=>setForm(f=>({...f,personal_email:e.target.value}))} style={ic} />
                      </div>
                      <div>
                        <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>Emergency Name</label>
                        <input type="text" placeholder="Father / Spouse name" value={form.emergency_contact_name} onChange={e=>setForm(f=>({...f,emergency_contact_name:e.target.value}))} style={ic} />
                      </div>
                      <div>
                        <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>Relation</label>
                        <input type="text" placeholder="Father / Mother / Spouse" value={form.emergency_contact_relation} onChange={e=>setForm(f=>({...f,emergency_contact_relation:e.target.value}))} style={ic} />
                      </div>
                      <div>
                        <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>Emergency Mobile</label>
                        <input type="text" placeholder="9876543211" maxLength={15} value={form.emergency_contact_mobile} onChange={e=>setForm(f=>({...f,emergency_contact_mobile:e.target.value}))} style={ic} />
                      </div>
                    </div>
                  )
                },
                {
                  num: editEmp?'3':'4', title:'Employment Details',
                  content: (
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:14 }}>
                      <div>
                        <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>Date of Joining</label>
                        <SmartDatePicker value={form.date_of_joining} onChange={v=>setForm(f=>({...f,date_of_joining:v}))} placeholder="Select joining date" darkMode={darkMode} T={T} />
                      </div>
                      {[
                        { label:'Department', content: <select value={form.department} onChange={e=>setForm(f=>({...f,department:e.target.value}))} style={ic}><option value="">Select...</option>{DEPARTMENTS.map(d=><option key={d} value={d}>{d}</option>)}</select> },
                        { label:'Designation', content: <input type="text" placeholder="e.g. Senior Associate" value={form.designation} onChange={e=>setForm(f=>({...f,designation:e.target.value}))} style={ic} /> },
                        { label:'Employment Type', content: <select value={form.employment_type} onChange={e=>setForm(f=>({...f,employment_type:e.target.value}))} style={ic}>{EMPLOYMENT_TYPES.map(t=><option key={t.value} value={t.value}>{t.label}</option>)}</select> },
                        { label:'Work Location', content: <select value={form.work_location} onChange={e=>setForm(f=>({...f,work_location:e.target.value}))} style={ic}><option value="office">Office</option><option value="remote">Remote</option><option value="hybrid">Hybrid</option></select> },
                        { label:'Reporting To', content: <select value={form.reporting_to} onChange={e=>setForm(f=>({...f,reporting_to:e.target.value}))} style={ic}><option value="">— None —</option>{employees.filter(emp=>emp.id!==editEmp?.id).map(emp=><option key={emp.id} value={emp.id}>{emp.full_name}</option>)}</select> },
                        { label:'Status', content: <select value={form.status} onChange={e=>setForm(f=>({...f,status:e.target.value}))} style={ic}>{Object.entries(STATUS_CFG).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}</select> },
                        { label:'Clock-in Method', content: <select value={form.attendance_mode} onChange={e=>setForm(f=>({...f,attendance_mode:e.target.value}))} style={ic}>
                            <option value="">Firm default (Settings → Work Schedule)</option>
                            <option value="manual">Clock In / Clock Out</option>
                            <option value="selfie">Selfie + Clock In / Out</option>
                          </select> },
                      ].map(f=>(
                        <div key={f.label}>
                          <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>{f.label}</label>
                          {f.content}
                        </div>
                      ))}
                    </div>
                  )
                },
                {
                  num: editEmp?'4':'5', title:'Bank Account',
                  content: (
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14 }}>
                      <div style={{ gridColumn:'span 2' }}>
                        <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>Bank Name</label>
                        <input type="text" placeholder="ICICI Bank..." value={form.bank_name} onChange={e=>setForm(f=>({...f,bank_name:e.target.value}))} style={ic} />
                      </div>
                      {[
                        { label:'Account Number', val:'bank_account', ph:'Account number' },
                        { label:'IFSC', val:'bank_ifsc', ph:'ICIC0001234', up:true },
                        { label:'Account Type', val:null, content: <select value={form.bank_account_type} onChange={e=>setForm(f=>({...f,bank_account_type:e.target.value}))} style={ic}><option value="">Select...</option><option>Savings</option><option>Current</option><option>Salary</option></select> },
                      ].map(f=>(
                        <div key={f.label}>
                          <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>{f.label}</label>
                          {f.content || <input type="text" placeholder={f.ph} value={form[f.val]||''} onChange={e=>setForm(fm=>({...fm,[f.val]:f.up?e.target.value.toUpperCase():e.target.value}))} style={ic} />}
                        </div>
                      ))}
                    </div>
                  )
                },
              ].map(sec=>(
                <div key={sec.num}>
                  <div style={{ display:'flex', alignItems:'center', gap:12, margin:'24px 0 16px' }}>
                    <div style={{ width:26, height:26, borderRadius:7, background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:11, fontWeight:700 }}>{sec.num}</div>
                    <h4 style={{ fontSize:12, fontWeight:700, color:T.text3, textTransform:'uppercase', letterSpacing:'0.08em', margin:0 }}>{sec.title}</h4>
                    <div style={{ flex:1, height:1, background:T.border }}/>
                  </div>
                  {sec.content}
                </div>
              ))}

              {!editEmp && (
                <div>
                  <div style={{ display:'flex', alignItems:'center', gap:12, margin:'24px 0 16px' }}>
                    <div style={{ width:26, height:26, borderRadius:7, background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:11, fontWeight:700 }}>6</div>
                    <h4 style={{ fontSize:12, fontWeight:700, color:T.text3, textTransform:'uppercase', letterSpacing:'0.08em', margin:0 }}>Salary Structure</h4>
                    <div style={{ flex:1, height:1, background:T.border }}/>
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:14, marginBottom:16 }}>
                    {[{key:'ctc',label:'Monthly CTC (₹)'},{key:'basic',label:'Basic Pay (₹)'},{key:'hra',label:'HRA (₹)'},{key:'travel_allowance',label:'Travel Allowance (₹)'},{key:'special_allowance',label:'Special Allowance (₹)'},{key:'other_allowance',label:'Other Allowance (₹)'}].map(f=>(
                      <div key={f.key}>
                        <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>{f.label}</label>
                        <input type="number" placeholder="0" value={salary[f.key]} onChange={e=>setSalary(s=>({...s,[f.key]:e.target.value}))} style={ic} />
                      </div>
                    ))}
                  </div>
                  <div style={{ background: darkMode?'rgba(255,255,255,0.04)':T.bg2, border:`1px solid ${T.border}`, borderRadius:14, padding:20 }}>
                    <p style={{ fontSize:10, fontWeight:700, color:T.text3, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:16 }}>Statutory Deductions</p>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
                      {[
                        {key:'enable_epf',label:'Enable EPF',p1:'epf_employee_percent',l1:'Employee % (12)',p2:'epf_employer_percent',l2:'Employer %'},
                        {key:'enable_esi',label:'Enable ESI',p1:'esi_employee_percent',l1:'Employee % (0.75)',p2:'esi_employer_percent',l2:'Employer % (3.25)'},
                      ].map(d=>(
                        <div key={d.key}>
                          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
                            <button type="button" onClick={()=>setSalary(s=>({...s,[d.key]:!s[d.key]}))}
                              style={{ position:'relative', width:38, height:20, borderRadius:10, border:'none', cursor:'pointer', background: salary[d.key]?'linear-gradient(135deg,#0f1f45,#1a3a6b)':(darkMode?'#334155':'#d1d5db'), flexShrink:0 }}>
                              <span style={{ position:'absolute', top:2, left: salary[d.key]?20:2, width:16, height:16, borderRadius:'50%', background:'#fff', transition:'left 0.2s' }}/>
                            </button>
                            <span style={{ fontSize:13, color:T.text2 }}>{d.label}</span>
                          </div>
                          {salary[d.key] && (
                            <div style={{ marginLeft:48, display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                              <div>
                                <label style={{ display:'block', fontSize:10, color:T.text3, marginBottom:4 }}>{d.l1}</label>
                                <input type="number" value={salary[d.p1]} onChange={e=>setSalary(s=>({...s,[d.p1]:e.target.value}))} style={ic} />
                              </div>
                              <div>
                                <label style={{ display:'block', fontSize:10, color:T.text3, marginBottom:4 }}>{d.l2}</label>
                                <input type="number" value={salary[d.p2]} onChange={e=>setSalary(s=>({...s,[d.p2]:e.target.value}))} style={ic} />
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                      {[
                        {key:'enable_tds',label:'Enable TDS',ak:'tds_monthly',al:'TDS Monthly (₹)'},
                        {key:'enable_pt',label:'Enable Prof. Tax',ak:'pt_monthly',al:'PT Monthly (₹)'},
                      ].map(d=>(
                        <div key={d.key}>
                          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
                            <button type="button" onClick={()=>setSalary(s=>({...s,[d.key]:!s[d.key]}))}
                              style={{ position:'relative', width:38, height:20, borderRadius:10, border:'none', cursor:'pointer', background: salary[d.key]?'linear-gradient(135deg,#0f1f45,#1a3a6b)':(darkMode?'#334155':'#d1d5db'), flexShrink:0 }}>
                              <span style={{ position:'absolute', top:2, left: salary[d.key]?20:2, width:16, height:16, borderRadius:'50%', background:'#fff', transition:'left 0.2s' }}/>
                            </button>
                            <span style={{ fontSize:13, color:T.text2 }}>{d.label}</span>
                          </div>
                          {salary[d.key] && (
                            <div style={{ marginLeft:48 }}>
                              <label style={{ display:'block', fontSize:10, color:T.text3, marginBottom:4 }}>{d.al}</label>
                              <input type="number" value={salary[d.ak]} onChange={e=>setSalary(s=>({...s,[d.ak]:e.target.value}))} style={ic} />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    {grossSalary() > 0 && (
                      <div style={{ marginTop:16, paddingTop:14, borderTop:`1px solid ${T.border}`, display:'flex', justifyContent:'space-between', fontSize:13 }}>
                        <div style={{ color:T.text2 }}>Gross <strong style={{ color:T.text }}>{fc(grossSalary())}</strong> · Deductions <strong style={{ color:'#ef4444' }}>-{fc(totalDeductions())}</strong></div>
                        <div style={{ fontWeight:800, color:T.text }}>Net {fc(grossSalary()-totalDeductions())}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div style={{ marginTop:20 }}>
                <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>Notes</label>
                <textarea rows="2" placeholder="Any additional remarks..." value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} style={{ ...ic, resize:'none' }} />
              </div>
            </div>

            <div style={{ display:'flex', alignItems:'center', justifyContent:'flex-end', gap:12, padding:'16px 32px', borderTop:`1px solid ${T.border}`, background:T.bg2, flexShrink:0, borderRadius:'0 0 20px 20px' }}>
              <button onClick={()=>setShowForm(false)} style={{ padding:'10px 20px', borderRadius:10, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, cursor:'pointer' }}
                onMouseEnter={e=>e.currentTarget.style.background=T.bg3}
                onMouseLeave={e=>e.currentTarget.style.background=T.bg2}>Cancel</button>
              <button onClick={handleSave} disabled={saving} style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 28px', borderRadius:10, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', opacity:saving?0.6:1, boxShadow:'0 4px 12px rgba(15,31,69,0.3)' }}>
                {saving && <span style={{ width:14, height:14, border:'2px solid #fff', borderTopColor:'transparent', borderRadius:'50%', display:'inline-block', animation:'spin 0.6s linear infinite' }}/>}
                {saving ? 'Saving...' : editEmp ? 'Save Changes' : 'Onboard Employee'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SALARY EDIT MODAL */}
      {showSalary && selectedEmp && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:50, padding:16 }}>
          <div style={{ background:T.bg2, borderRadius:20, width:'100%', maxWidth:680, maxHeight:'88vh', display:'flex', flexDirection:'column', boxShadow: darkMode?'0 24px 60px rgba(0,0,0,0.6)':'0 24px 60px rgba(15,31,69,0.2)' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'18px 28px', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', borderRadius:'20px 20px 0 0', flexShrink:0 }}>
              <div>
                <h3 style={{ fontSize:15, fontWeight:700, color:'#fff', margin:0 }}>Edit Salary Structure</h3>
                <p style={{ fontSize:12, color:'rgba(255,255,255,0.6)', margin:'3px 0 0' }}>{selectedEmp.full_name} · {selectedEmp.employee_id}</p>
              </div>
              <button onClick={()=>setShowSalary(false)}
                style={{ width:34, height:34, borderRadius:9, border:'none', background:'rgba(255,255,255,0.1)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}
                onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.2)'}
                onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,0.1)'}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div style={{ flex:1, overflowY:'auto', padding:'16px 28px 24px', background:T.bg3 }}>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:14, marginBottom:20 }}>
                {[{key:'ctc',label:'Monthly CTC (₹)'},{key:'basic',label:'Basic Pay (₹)'},{key:'hra',label:'HRA (₹)'},{key:'travel_allowance',label:'Travel Allowance (₹)'},{key:'special_allowance',label:'Special Allowance (₹)'},{key:'other_allowance',label:'Other Allowance (₹)'}].map(f=>(
                  <div key={f.key}>
                    <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>{f.label}</label>
                    <input type="number" placeholder="0" value={salary[f.key]||0} onChange={e=>setSalary(s=>({...s,[f.key]:e.target.value}))} style={ic} />
                  </div>
                ))}
              </div>
              <div style={{ background: darkMode?'rgba(255,255,255,0.04)':T.bg2, border:`1px solid ${T.border}`, borderRadius:14, padding:20 }}>
                <p style={{ fontSize:10, fontWeight:700, color:T.text3, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:16 }}>Statutory Deductions</p>
                <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
                  {[{key:'enable_epf',label:'EPF'},{key:'enable_esi',label:'ESI'},{key:'enable_tds',label:'TDS',ak:'tds_monthly',al:'TDS Monthly (₹)'},{key:'enable_pt',label:'Professional Tax',ak:'pt_monthly',al:'PT Monthly (₹)'}].map(d=>(
                    <div key={d.key}>
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <button type="button" onClick={()=>setSalary(s=>({...s,[d.key]:!s[d.key]}))}
                          style={{ position:'relative', width:38, height:20, borderRadius:10, border:'none', cursor:'pointer', background: salary[d.key]?'linear-gradient(135deg,#0f1f45,#1a3a6b)':(darkMode?'#334155':'#d1d5db'), flexShrink:0 }}>
                          <span style={{ position:'absolute', top:2, left: salary[d.key]?20:2, width:16, height:16, borderRadius:'50%', background:'#fff', transition:'left 0.2s' }}/>
                        </button>
                        <span style={{ fontSize:13, color:T.text2 }}>{d.label}</span>
                      </div>
                      {d.ak && salary[d.key] && (
                        <div style={{ marginTop:8, marginLeft:48 }}>
                          <label style={{ display:'block', fontSize:10, color:T.text3, marginBottom:4 }}>{d.al}</label>
                          <input type="number" value={salary[d.ak]||0} onChange={e=>setSalary(s=>({...s,[d.ak]:e.target.value}))} style={ic} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {grossSalary() > 0 && (
                  <div style={{ marginTop:14, paddingTop:14, borderTop:`1px solid ${T.border}`, display:'flex', justifyContent:'space-between', fontSize:13 }}>
                    <div style={{ color:T.text2 }}>Gross <strong style={{ color:T.text }}>{fc(grossSalary())}</strong> · Deductions <strong style={{ color:'#ef4444' }}>-{fc(totalDeductions())}</strong></div>
                    <div style={{ fontWeight:800, color:T.text }}>Net {fc(grossSalary()-totalDeductions())}</div>
                  </div>
                )}
              </div>
            </div>
            <div style={{ display:'flex', justifyContent:'flex-end', gap:12, padding:'16px 28px', borderTop:`1px solid ${T.border}`, background:T.bg2, flexShrink:0, borderRadius:'0 0 20px 20px' }}>
              <button onClick={()=>setShowSalary(false)} style={{ padding:'10px 20px', borderRadius:10, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, cursor:'pointer' }}
                onMouseEnter={e=>e.currentTarget.style.background=T.bg3}
                onMouseLeave={e=>e.currentTarget.style.background=T.bg2}>Cancel</button>
              <button onClick={handleSaveSalary} disabled={saving} style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 24px', borderRadius:10, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', opacity:saving?0.6:1 }}>
                {saving && <span style={{ width:14, height:14, border:'2px solid #fff', borderTopColor:'transparent', borderRadius:'50%', display:'inline-block', animation:'spin 0.6s linear infinite' }}/>}
                {saving ? 'Saving...' : 'Save Salary'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── TIMESHEETS ────────────────────────────────────────────────────────────────
// Hours actually worked from clock-in sessions, against the firm's work schedule.
const tsPad = n => String(n).padStart(2, '0');
const tsYmd = d => `${d.getFullYear()}-${tsPad(d.getMonth() + 1)}-${tsPad(d.getDate())}`;
const tsHm = mins => { const m = Math.max(0, Math.round(mins || 0)); return `${Math.floor(m / 60)}h ${tsPad(m % 60)}m`; };
const tsTime = ts => (ts ? new Date(ts).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' }) : '—');
const TS_TONE = {
  leave:   { fg:'#b45309', bg:'#fef3c7', dbg:'rgba(245,158,11,0.15)' },
  holiday: { fg:'#7c3aed', bg:'#f5f3ff', dbg:'rgba(124,58,237,0.15)' },
  off:     { fg:'#64748b', bg:'#f1f5f9', dbg:'rgba(100,116,139,0.18)' },
  absent:  { fg:'#dc2626', bg:'#fef2f2', dbg:'rgba(220,38,38,0.15)' },
  marked:  { fg:'#2563eb', bg:'#eff6ff', dbg:'rgba(37,99,235,0.15)' },
  none:    { fg:'#64748b', bg:'#f1f5f9', dbg:'rgba(100,116,139,0.18)' },
};

function TsTag({ day, darkMode }) {
  if (!day.tag) return null;
  const c = TS_TONE[day.tone] || TS_TONE.none;
  return <span style={{ fontSize:11, fontWeight:600, padding:'2px 9px', borderRadius:20, color:c.fg, background:darkMode?c.dbg:c.bg, whiteSpace:'nowrap' }}>{day.tag}</span>;
}

// Selfies are private: fetched with the login token and shown from a blob URL.
function TsSelfie({ logId, which, headers }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url = null, alive = true;
    fetch(`${BASE}/api/employees/attendance/selfie/${logId}/${which}`, { headers })
      .then(r => (r.ok ? r.blob() : null))
      .then(b => { if (b && alive) { url = URL.createObjectURL(b); setSrc(url); } })
      .catch(() => {});
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logId, which]);
  if (!src) return <span style={{ width:44, height:44, borderRadius:8, background:'#e2e8f0', display:'inline-block' }} />;
  return <a href={src} target="_blank" rel="noreferrer" title={`Clock-${which} selfie`}><img src={src} alt={`Clock-${which} selfie`} style={{ width:44, height:44, borderRadius:8, objectFit:'cover' }} /></a>;
}

function TimesheetsTab({ headers, darkMode = false, T }) {
  const { scope } = usePermissions();
  const seesTeam = scope('hr.attendance_view') === 'all';
  const [view, setView] = useState(seesTeam ? 'daily' : 'monthly');
  const [date, setDate] = useState(() => tsYmd(new Date()));
  const [daily, setDaily] = useState(null);
  const [empId, setEmpId] = useState(null);
  const [ym, setYm] = useState(() => ({ m: new Date().getMonth() + 1, y: new Date().getFullYear() }));
  const [month, setMonth] = useState(null);
  const [openDay, setOpenDay] = useState(null);
  const [loading, setLoading] = useState(false);
  const todayStr = tsYmd(new Date());

  useEffect(() => {
    if (view !== 'daily') return;
    setLoading(true);
    fetch(`${BASE}/api/employees/timesheets/daily?date=${date}`, { headers })
      .then(r => r.json()).then(d => setDaily(Array.isArray(d.employees) ? d : { employees: [] }))
      .catch(() => setDaily({ employees: [] })).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, date]);

  // Staff with "own" scope: their own employee record is the only option.
  useEffect(() => {
    if (empId || seesTeam) return;
    fetch(`${BASE}/api/employees/my/punch-status`, { headers }).then(r => r.json())
      .then(d => { if (d.employee_id) setEmpId(d.employee_id); }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seesTeam]);

  useEffect(() => {
    if (view !== 'monthly' || !empId) return;
    setLoading(true);
    fetch(`${BASE}/api/employees/timesheets/employee/${empId}?month=${ym.m}&year=${ym.y}`, { headers })
      .then(r => r.json()).then(d => setMonth(d.days ? d : null))
      .catch(() => setMonth(null)).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, empId, ym.m, ym.y]);

  const shiftDate = n => setDate(d => { const x = new Date(`${d}T00:00:00`); x.setDate(x.getDate() + n); const s = tsYmd(x); return s > todayStr ? todayStr : s; });
  const shiftMonth = n => setYm(({ m, y }) => { const d = new Date(y, m - 1 + n, 1); return { m: d.getMonth() + 1, y: d.getFullYear() }; });
  const isCurrentMonth = ym.m === new Date().getMonth() + 1 && ym.y === new Date().getFullYear();
  const openEmployee = id => { setEmpId(id); setView('monthly'); setOpenDay(null); };

  const card = { background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:16, boxShadow:darkMode?'0 4px 24px rgba(0,0,0,0.3)':'0 1px 4px rgba(15,31,69,0.05)' };
  const navBtn = { width:32, height:32, borderRadius:9, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text2, cursor:'pointer', fontSize:14 };
  const th = { padding:'10px 16px', textAlign:'left', fontSize:11, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em', whiteSpace:'nowrap' };
  const td = { padding:'11px 16px', fontSize:13, color:T.text, borderBottom:`1px solid ${T.border}` };
  const teamList = daily?.employees || [];

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, flexWrap:'wrap', marginBottom:16 }}>
        <div>
          <h3 style={{ fontSize:16, fontWeight:700, color:T.text, margin:0 }}>{seesTeam ? 'Organization Timesheets' : 'My Timesheet'}</h3>
          <p style={{ fontSize:12, color:T.text3, margin:'3px 0 0' }}>Hours from clock-in / clock-out sessions, against the work schedule</p>
        </div>
        {seesTeam && (
          <div style={{ display:'flex', background:T.bg3, border:`1px solid ${T.border}`, borderRadius:12, padding:4 }}>
            {[['daily','Daily'],['monthly','Monthly']].map(([k,l]) => (
              <button key={k} onClick={() => setView(k)} style={{ padding:'7px 18px', borderRadius:9, border:'none', cursor:'pointer', fontSize:13, fontWeight:600,
                background: view===k ? 'linear-gradient(135deg,#0f1f45,#1a3a6b)' : 'transparent', color: view===k ? '#fff' : T.text3 }}>{l}</button>
            ))}
          </div>
        )}
      </div>

      {view === 'daily' && (
        <div style={card}>
          <div style={{ display:'flex', alignItems:'center', gap:10, padding:'14px 18px', borderBottom:`1px solid ${T.border}` }}>
            <button style={navBtn} onClick={() => shiftDate(-1)} aria-label="Previous day">‹</button>
            <input type="date" value={date} max={todayStr} onChange={e => e.target.value && setDate(e.target.value)}
              style={{ border:`1px solid ${T.inputBorder}`, borderRadius:9, padding:'6px 10px', fontSize:13, background:T.inputBg, color:T.text }} />
            <button style={{ ...navBtn, opacity: date >= todayStr ? 0.4 : 1 }} disabled={date >= todayStr} onClick={() => shiftDate(1)} aria-label="Next day">›</button>
            <span style={{ fontSize:13, fontWeight:600, color:T.text2, marginLeft:6 }}>
              {new Date(`${date}T00:00:00`).toLocaleDateString('en-IN', { weekday:'long', day:'numeric', month:'long' })}
            </span>
          </div>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)' }}>
                <tr>{['Employee','Day','Clock in','Clock out','Sessions','Worked','Schedule'].map(h => <th key={h} style={th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {loading && !daily ? (
                  <tr><td colSpan={7} style={{ ...td, textAlign:'center', color:T.text3, padding:32 }}>Loading…</td></tr>
                ) : teamList.length === 0 ? (
                  <tr><td colSpan={7} style={{ ...td, textAlign:'center', color:T.text3, padding:32 }}>No employees for this day</td></tr>
                ) : teamList.map(r => (
                  <tr key={r.employee_id} onClick={() => openEmployee(r.employee_id)} style={{ cursor:'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background = darkMode ? '#1e3a5f' : '#f0f4ff'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={td}><span style={{ fontWeight:600 }}>{r.full_name}</span><span style={{ display:'block', fontSize:11, color:T.text3 }}>{r.designation || r.emp_code}</span></td>
                    <td style={td}>{r.is_clocked_in ? <span style={{ fontSize:11, fontWeight:600, padding:'2px 9px', borderRadius:20, color:'#16a34a', background:darkMode?'rgba(22,163,74,0.15)':'#f0fdf4' }}>Working now</span> : <TsTag day={r} darkMode={darkMode} />}</td>
                    <td style={td}>{tsTime(r.first_in)}</td>
                    <td style={td}>{r.is_clocked_in ? '—' : tsTime(r.last_out)}</td>
                    <td style={td}>{r.sessions.length || '—'}</td>
                    <td style={{ ...td, fontWeight:700 }}>{r.worked_minutes ? tsHm(r.worked_minutes) : '—'}</td>
                    <td style={{ ...td, color:T.text3 }}>{r.required_minutes ? tsHm(r.required_minutes) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {view === 'monthly' && (
        <div style={card}>
          <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', padding:'14px 18px', borderBottom:`1px solid ${T.border}` }}>
            {seesTeam && (
              <select value={empId || ''} onChange={e => { setEmpId(Number(e.target.value) || null); setOpenDay(null); }}
                style={{ border:`1px solid ${T.inputBorder}`, borderRadius:9, padding:'7px 10px', fontSize:13, background:T.inputBg, color:T.text, minWidth:200 }}>
                <option value="">Select employee…</option>
                {teamList.map(r => <option key={r.employee_id} value={r.employee_id}>{r.full_name}</option>)}
              </select>
            )}
            <button style={navBtn} onClick={() => shiftMonth(-1)} aria-label="Previous month">‹</button>
            <span style={{ fontSize:13, fontWeight:700, color:T.text, minWidth:120, textAlign:'center' }}>
              {new Date(ym.y, ym.m - 1, 1).toLocaleDateString('en-IN', { month:'long', year:'numeric' })}
            </span>
            <button style={{ ...navBtn, opacity: isCurrentMonth ? 0.4 : 1 }} disabled={isCurrentMonth} onClick={() => shiftMonth(1)} aria-label="Next month">›</button>
          </div>
          {!empId ? (
            <div style={{ padding:40, textAlign:'center', color:T.text3, fontSize:13 }}>Pick an employee to see their month.</div>
          ) : loading && !month ? (
            <div style={{ padding:40, textAlign:'center', color:T.text3, fontSize:13 }}>Loading…</div>
          ) : month && (
            <>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:12, padding:18 }}>
                {[
                  { l:'Tracked hours', v: tsHm(month.summary.tracked_minutes), c: T.text },
                  { l:'Work schedule', v: tsHm(month.summary.required_minutes), c: T.text },
                  { l: month.summary.difference_minutes < 0 ? 'Short by' : 'Extra', v: tsHm(Math.abs(month.summary.difference_minutes)), c: month.summary.difference_minutes < 0 ? '#dc2626' : '#16a34a' },
                ].map(s => (
                  <div key={s.l} style={{ background:T.bg3, border:`1px solid ${T.border}`, borderRadius:12, padding:'12px 16px' }}>
                    <p style={{ fontSize:11, fontWeight:600, color:T.text3, margin:0, textTransform:'uppercase', letterSpacing:'0.05em' }}>{s.l}</p>
                    <p style={{ fontSize:20, fontWeight:800, color:s.c, margin:'4px 0 0' }}>{s.v}</p>
                  </div>
                ))}
              </div>
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)' }}>
                    <tr>{['Date','Day','Clock in','Clock out','Sessions','Worked','Schedule'].map(h => <th key={h} style={th}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {month.days.length === 0 ? (
                      <tr><td colSpan={7} style={{ ...td, textAlign:'center', color:T.text3, padding:32 }}>Nothing recorded this month</td></tr>
                    ) : [...month.days].reverse().map(d => (
                      <React.Fragment key={d.date}>
                        <tr onClick={() => d.sessions.length && setOpenDay(openDay === d.date ? null : d.date)} style={{ cursor: d.sessions.length ? 'pointer' : 'default' }}>
                          <td style={{ ...td, fontWeight:600, whiteSpace:'nowrap' }}>{new Date(`${d.date}T00:00:00`).toLocaleDateString('en-IN', { weekday:'short', day:'numeric', month:'short' })}</td>
                          <td style={td}>{d.is_clocked_in ? <span style={{ fontSize:11, fontWeight:600, color:'#16a34a' }}>Working now</span> : <TsTag day={d} darkMode={darkMode} />}</td>
                          <td style={td}>{tsTime(d.first_in)}</td>
                          <td style={td}>{d.is_clocked_in ? '—' : tsTime(d.last_out)}</td>
                          <td style={td}>{d.sessions.length ? `${d.sessions.length} ${openDay === d.date ? '▴' : '▾'}` : '—'}</td>
                          <td style={{ ...td, fontWeight:700, color: d.required_minutes && d.worked_minutes < d.required_minutes && !d.is_clocked_in ? '#dc2626' : T.text }}>{d.worked_minutes ? tsHm(d.worked_minutes) : '—'}</td>
                          <td style={{ ...td, color:T.text3 }}>{d.required_minutes ? tsHm(d.required_minutes) : '—'}</td>
                        </tr>
                        {openDay === d.date && (
                          <tr><td colSpan={7} style={{ ...td, background:T.bg3 }}>
                            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                              {d.sessions.map(s => (
                                <div key={s.id} style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
                                  {s.has_in_selfie && <TsSelfie logId={s.id} which="in" headers={headers} />}
                                  {s.has_out_selfie && <TsSelfie logId={s.id} which="out" headers={headers} />}
                                  <span style={{ fontWeight:600 }}>{tsTime(s.punch_in)} – {s.punch_out ? tsTime(s.punch_out) : 'now'}</span>
                                  <span style={{ color:T.text3 }}>{tsHm(s.minutes)}</span>
                                  {s.auto_closed && <span style={{ fontSize:11, color:'#b45309' }}>Auto clocked-out at closing time</span>}
                                  {s.source && <span style={{ fontSize:11, color:T.text3 }}>via {s.source === 'app' ? 'app' : 'browser'}</span>}
                                </div>
                              ))}
                            </div>
                          </td></tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default HR;